import { initializeApp, getApp, deleteApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { getAuth, signInAnonymously, onAuthStateChanged, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail, signOut, EmailAuthProvider, linkWithCredential, updateProfile } from "firebase/auth";
import { marked } from "marked";

// TODO: https://firebase.google.com/docs/auth/web/anonymous-auth#convert-an-anonymous-account-to-a-permanent-account

(function (window: Window, document: Document) {

    // Define base server URLs for each environment.
    const baseUrls: Record<string, string> = {
        dev: "http://localhost:3008",
        devAPI: "http://localhost:3001",
        staging: "https://tripist-staging.lighthousepe.ai",
        stagingAPI: "https://oma-lh-staging-api.herokuapp.com",
        production: "https://tripist.lighthousepe.ai",
        productionAPI: "https://api.lighthousepe.com",
    };

    function toAbsoluteUrl(url: string): string {
        try {
            return new URL(url, window.location.href).href;
        } catch (error) {
            return url;
        }
    }

    /**
     * Dynamically load a CSS file into the document head.
     * @param cssUrl - URL of the CSS file.
     * @param id - A unique id for the <link> element.
     * @param callback - Optional callback to run when CSS is loaded.
     */
    function loadCss(cssUrl: string, id: string, callback?: () => void): void {
        const resolvedCssUrl = toAbsoluteUrl(cssUrl);
        const existing = document.getElementById(id) as HTMLLinkElement | null;
        if (existing) {
            const existingHref = toAbsoluteUrl(existing.getAttribute("href") || "");
            if (existingHref === resolvedCssUrl) {
                console.log(`CSS already loaded: ${id}`);
                if (callback) callback();
                return;
            }
            console.log(`Replacing CSS: ${id} (${existingHref} -> ${resolvedCssUrl})`);
            existing.remove();
        }

        console.log(`Loading CSS: ${cssUrl} with id: ${id}`);
        
        const link = document.createElement("link");
        link.id = id;
        link.rel = "stylesheet";
        link.href = resolvedCssUrl;
        
        if (callback) {
            link.onload = () => {
                console.log(`CSS loaded successfully: ${id}`);
                callback();
            };
            link.onerror = (error) => {
                console.error(`Failed to load CSS: ${cssUrl}`, error);
                // Still call callback to prevent hanging, but log the error
                callback();
            };
        }
        
        document.head.appendChild(link);
    }

    /**
     * Load remote CSS and rewrite relative URLs to be absolute.
     * @param cssUrl - URL of the remote CSS file.
     * @param id - A unique id for the <style> element.
     * @param callback - Optional callback to run when CSS is loaded.
     */
    function loadRemoteCss(cssUrl: string, id: string, callback?: () => void): void {
        const resolvedCssUrl = toAbsoluteUrl(cssUrl);
        const existing = document.getElementById(id) as HTMLStyleElement | null;
        if (existing) {
            const existingSource = existing.getAttribute("data-source-url") || "";
            const hasCssContent = !!existing.textContent && existing.textContent.trim().length > 0;
            if (existingSource === resolvedCssUrl && hasCssContent) {
                console.log(`Remote CSS already loaded: ${id}`);
                if (callback) callback();
                return;
            }
            console.log(`Replacing remote CSS: ${id} (${existingSource || "unknown"} -> ${resolvedCssUrl})`);
            existing.remove();
        }
        
        console.log(`Loading remote CSS: ${resolvedCssUrl} with id: ${id}`);
        
        fetch(resolvedCssUrl)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Failed to load remote CSS: ${response.status}`);
                }
                return response.text();
            })
            .then(cssText => {
                // Create base URL for resolving relative paths
                const cssBaseUrl = new URL(cssUrl).href.substring(0, new URL(cssUrl).href.lastIndexOf('/') + 1);
                
                // More comprehensive regex to catch all URL patterns in CSS
                const rewrittenCss = cssText.replace(/url\s*\(\s*(['"]?)(?!https?:\/\/|data:|\/\/)([^'")]+)\1\s*\)/gi, (match, quote, relativePath) => {
                    try {
                        const cleanPath = relativePath.trim();
                        const absoluteUrl = new URL(cleanPath, cssBaseUrl).href;
                        console.log(`Rewriting CSS URL: ${match} -> url('${absoluteUrl}')`);
                        return `url('${absoluteUrl}')`;
                    } catch (error) {
                        console.error(`Failed to rewrite CSS URL: ${match}`, error);
                        return match; // Return original if failed
                    }
                });
                
                // Create style element
                const style = document.createElement("style");
                style.id = id;
                style.setAttribute("data-source-url", resolvedCssUrl);
                style.textContent = rewrittenCss;
                
                document.head.appendChild(style);
                console.log(`Remote CSS loaded and rewritten successfully: ${id}`);
                
                if (callback) callback();
            })
            .catch(error => {
                console.error(`Failed to load remote CSS: ${resolvedCssUrl}`, error);
                if (callback) callback();
            });
    }

    /**
     * Rewrite relative URLs in HTML content to be absolute.
     * @param htmlContent - The HTML content as a string.
     * @param baseUrl - The base URL to resolve relative paths against.
     * @returns The HTML content with rewritten URLs.
     */
    function rewriteHtmlUrls(htmlContent: string, baseUrl: string): string {
        const htmlBaseUrl = baseUrl.substring(0, baseUrl.lastIndexOf('/') + 1);
        
        // More comprehensive regex to catch all src attribute patterns
        const rewrittenHtml = htmlContent.replace(/src\s*=\s*(['"])(?!https?:\/\/|data:|\/\/)([^'"]+)\1/gi, (match, quote, relativePath) => {
            try {
                const cleanPath = relativePath.trim();
                const absoluteUrl = new URL(cleanPath, htmlBaseUrl).href;
                console.log(`Rewriting HTML URL: ${match} -> src="${absoluteUrl}"`);
                return `src="${absoluteUrl}"`;
            } catch (error) {
                console.error(`Failed to rewrite HTML URL: ${match}`, error);
                return match; // Return original if failed
            }
        });
        
        return rewrittenHtml;
    }

    interface TripistInitParams {
        integrationId: string;
        elementSelector: string;
        environment?: string;
        isTestThread?: boolean;
        disableResultsCache?: boolean;
        onReady?: () => void;
        onIdentify?: (user: any) => void;
        onIdentifyError?: (error: any) => void;
    }

    interface ExternalIdentity {
        externalUserId: string;
        email?: string;
        firstName?: string;
        lastName?: string;
        hash?: string;
        profile?: Record<string, any>;
    }

    interface TripistInitMobileParams {
        integrationId: string;
        environment?: string;
        identityToken?: string;
    }

    interface BrowserInfo {
        browser: string;
        version: string;
        os: string;
        osVersion: string;
    }

    interface TooltipButton extends HTMLButtonElement {
        _tooltipElement?: HTMLElement;
        _tooltipShown?: boolean;
    }

    enum AppState {
        Default = 'default',
        Categories = 'categories',
        Chat = 'chat',
        Results = 'results',
        ExploristResults = 'explorist_results',
        Auth = 'auth',
        Profile = 'profile',
    }

    const tripist = {

        currentState: AppState,
        previousState: AppState,

        integrationId: "",
        env: "",
        baseUrl: "",
        apiBaseUrl: "",
        widgetTemplateUrl: "",
        widgetCssUrl: "",
        elementSelector: "",
        
        isInitializing: false,

        config: null as any,
        selectedCategories: [] as number[],

        firebaseApp: null as any,
        firebaseAnalytics: null as any,
        firebaseAuth: null as any,
        firebaseUser: null as any,
        firebaseAuthUnsubscribe: null as any,

        lhpeDevice: null as any,
        lhpeUser: null as any,

        identityProviderType: null as string | null,
        identityProviderConfig: null as any,
        deviceIdentifier: null as string | null,
        isIdentified: false,
        onReady: null as (() => void) | null,
        onIdentify: null as ((user: any) => void) | null,
        onIdentifyError: null as ((error: any) => void) | null,
        isTestThread: false,

        container: null as HTMLElement | null,

        header: null as HTMLElement | null,
        logoText: null as HTMLImageElement | null,
        menuButton: null as HTMLImageElement | null,
        closeButton: null as HTMLImageElement | null,
        hud: null as HTMLElement | null,
        hudMessage: null as HTMLElement | null,
        hudButton: null as HTMLElement | null,

        body: null as HTMLElement | null,

        footerContainer: null as HTMLElement | null,

        catContainer: null as HTMLElement | null,
        catTitle: null as HTMLElement | null,
        catDescr: null as HTMLElement | null,
        catList: null as HTMLElement | null,
        catButton: null as HTMLElement | null,

        chatContainer: null as HTMLElement | null,
        chatSendButton: null as HTMLButtonElement | null,
        chatInputField: null as HTMLInputElement | null,
        chatMessagesContainer: null as HTMLDivElement | null,
        chatIntroContainer: null as HTMLElement | null,
        chatIntroTitle: null as HTMLElement | null,
        chatIntroDescr: null as HTMLElement | null,
        inputContianer: null as HTMLElement | null,
        textInput: null as HTMLInputElement | null,
        sendButton: null as HTMLButtonElement | null,
        actionsContainer: null as HTMLElement | null,

        authContainer: null as HTMLElement | null,
        loginEmail: null as HTMLInputElement | null,
        loginPassword: null as HTMLInputElement | null,
        loginButton: null as HTMLButtonElement | null,
        forgotPasswordLink: null as HTMLAnchorElement | null,
        registerFirstName: null as HTMLInputElement | null,
        registerLastName: null as HTMLInputElement | null,
        registerEmail: null as HTMLInputElement | null,
        registerPassword: null as HTMLInputElement | null,
        registerConfirmPassword: null as HTMLInputElement | null,
        registerLocation: null as HTMLInputElement | null,
        registerButton: null as HTMLButtonElement | null,
        registerCancelButton: null as HTMLButtonElement | null,
        placeSelected: null as any,

        profileContainer: null as HTMLElement | null,
        profileFirstName: null as HTMLInputElement | null,
        profileLastName: null as HTMLInputElement | null,
        profileEmail: null as HTMLInputElement | null,
        profileLocation: null as HTMLInputElement | null,
        profileAvatarTitle: null as HTMLElement | null,
        profileAvatarSubtitle: null as HTMLElement | null,
        profileAvatarContainer: null as HTMLElement | null,
        profileButton: null as HTMLButtonElement | null,
        profileCancelButton: null as HTMLElement | null,

        exploristResultsContainer: null as HTMLElement | null,
        exploristResultsHeader: null as HTMLElement | null,
        exploristResultsTitle: null as HTMLElement | null,
        exploristResultsMessage: null as HTMLElement | null,
        exploristResultsList: null as HTMLElement | null,

        currentlyOpenButton: null as HTMLButtonElement | null,
        currentlyOpenTooltip: null as HTMLElement | null,

        thread: null as any,
        chatState: null as any,
        streaming: false,
        buildingChatHistory: false,

        currentTempOutboundId: null as string | null,
        currentTempInboundId: null as string | null,
        currentMessageHasToolCalls: false,
        pendingInlineOptionMessageId: null as string | null,

        downloadUrl: null as string | null,
        shareDownloadUrl: null as string | null,
        disableResultsCache: false,
        exploristResultsData: null as any,

        feedbackModal: null as HTMLElement | null,
        feedbackStarsContainer: null as HTMLElement | null,
        feedbackCommentField: null as HTMLTextAreaElement | null,
        feedbackSubmitButton: null as HTMLButtonElement | null,
        feedbackEscapeHandler: null as ((event: KeyboardEvent) => void) | null,
        currentFeedbackRating: 0,

        // Helper methods for namespaced localStorage
        getStorageKey(key: string): string {
            return `tripist_${this.integrationId}_${key}`;
        },

        getLocalStorage(key: string): string | null {
            return localStorage.getItem(this.getStorageKey(key));
        },

        setLocalStorage(key: string, value: string): void {
            localStorage.setItem(this.getStorageKey(key), value);
        },

        removeLocalStorage(key: string): void {
            localStorage.removeItem(this.getStorageKey(key));
        },

        getClientContextHeaders(): Record<string, string> {
            const now = new Date();
            const offsetMinutes = -now.getTimezoneOffset();
            const sign = offsetMinutes >= 0 ? "+" : "-";
            const absOffsetMinutes = Math.abs(offsetMinutes);
            const offsetHours = String(Math.floor(absOffsetMinutes / 60)).padStart(2, "0");
            const offsetRemainderMinutes = String(absOffsetMinutes % 60).padStart(2, "0");

            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, "0");
            const day = String(now.getDate()).padStart(2, "0");
            const hours = String(now.getHours()).padStart(2, "0");
            const minutes = String(now.getMinutes()).padStart(2, "0");
            const seconds = String(now.getSeconds()).padStart(2, "0");
            const millis = String(now.getMilliseconds()).padStart(3, "0");

            const localDateTimeWithOffset = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${millis}${sign}${offsetHours}:${offsetRemainderMinutes}`;
            const locale = navigator.language || "en-US";

            return {
                "X-Tripist-Client-Locale": locale,
                "X-Tripist-Client-Current-Datetime": localDateTimeWithOffset,
                "X-Tripist-Client-UTC-Offset-Minutes": String(offsetMinutes)
            };
        },



        init({
            integrationId,
            elementSelector,
            environment = "production",
            isTestThread = false,
            disableResultsCache = false,
            onReady,
            onIdentify,
            onIdentifyError,
        }: TripistInitParams): void {

            const self = this;
            
            // Prevent overlapping initializations
            if (this.isInitializing) {
                console.warn("Tripist: Initialization already in progress");
                return;
            }
            
            this.isInitializing = true;
            this.currentState = AppState.Default;

            if (!integrationId) {
                console.error("Tripist: integrationId is required.");
                this.isInitializing = false;
                return;
            }
            if (!elementSelector) {
                console.error("Tripist: elementSelector is required.");
                this.isInitializing = false;
                return;
            }

            this.elementSelector = elementSelector;

            this.container = document.querySelector(elementSelector);
            if (!this.container) {
                console.error("Tripist: No element found with selector:", elementSelector);
                this.isInitializing = false;
                return;
            }

            this.integrationId = integrationId;
            this.disableResultsCache = disableResultsCache === true;
            this.isTestThread = isTestThread === true;
            this.onReady = onReady || null;
            this.onIdentify = onIdentify || null;
            this.onIdentifyError = onIdentifyError || null;

            this.env = environment.toLowerCase();
            this.baseUrl = baseUrls[this.env] || baseUrls.production;
            this.apiBaseUrl = baseUrls[`${this.env}API`] || baseUrls.productionAPI;

            this.chatState = new Proxy<{ messages: any[] }>(
                { messages: [] },
                {
                    set(target, property, value) {
                        target[property as keyof typeof target] = value;
                        // Only trigger buildChatHistory if config is loaded
                        if (self.config && self.config.integration && self.config.integration.config) {
                            self.buildChatHistory();
                        }
                        return true;
                    },
                }
            )

            if(this.getLocalStorage("thread_id")) {
                this.thread = this.getLocalStorage("thread_id");
            }

            this.getConfig();

        },

        prepareDOM(): void {

            try {

                const defaultTemplateUrl = `${this.baseUrl}/assets/default/widget.html`;
                const usingCustomTemplate = !!this.config.integration.config.custom_html;

                const fetchTemplate = (url: string): Promise<string> =>
                    fetch(url).then((response) => {
                        if (!response.ok) {
                            throw new Error(`Failed to load widget template from ${url}`);
                        }
                        return response.text();
                    });

                const templatePromise = usingCustomTemplate
                    ? fetchTemplate(this.widgetTemplateUrl).catch((err) => {
                        console.warn(
                            `Tripist: Custom template failed to load (${err.message}). Falling back to default template.`
                        );
                        this.widgetTemplateUrl = defaultTemplateUrl;
                        return fetchTemplate(defaultTemplateUrl);
                    })
                    : fetchTemplate(this.widgetTemplateUrl);

                templatePromise
                    .then((html) => {

                        // If using custom HTML from remote source, rewrite relative URLs
                        let processedHtml = html;
                        const isCustom = this.config.integration.config.custom_html &&
                            this.widgetTemplateUrl !== defaultTemplateUrl;
                        if (isCustom) {
                            console.log('Rewriting URLs in custom HTML template');
                            processedHtml = rewriteHtmlUrls(html, this.widgetTemplateUrl);
                        }

                        // Insert widget HTML into container.
                        this.container.innerHTML = processedHtml;

                        // Ensure feedback modal structure exists even for custom templates
                        this.ensureFeedbackModalElements();

                        // Get references to elements.
                        this.header = this.container.querySelector("#tripist-header") as HTMLElement | null;
                        this.logoText = this.container.querySelector("#tripist-text") as HTMLImageElement | null;
                        this.menuButton = this.container.querySelector("#tripist-menu-button") as HTMLImageElement | null;
                        this.closeButton = this.container.querySelector("#tripist-close-button") as HTMLImageElement | null;
                        this.hud = this.container.querySelector("#tripist-hud") as HTMLElement | null;
                        this.hudMessage = this.hud?.querySelector("#tripist-hud-message") as HTMLElement | null;
                        this.hudButton = this.hud?.querySelector("#tripist-hud-button") as HTMLElement | null;

                        this.body = this.container.querySelector("#tripist-body") as HTMLElement | null;

                        this.footerContainer = this.container.querySelector("#tripist-footer") as HTMLElement | null;

                        this.catContainer = this.container.querySelector("#tripist-categories-container") as HTMLElement | null;
                        this.catTitle = this.catContainer.querySelector("#tripist-categories-title") as HTMLElement | null;
                        this.catDescr = this.catContainer.querySelector("#tripist-categories-descr") as HTMLElement | null;
                        this.catList = this.catContainer.querySelector("#tripist-categories-list") as HTMLElement | null;
                        this.catButton = this.catContainer.querySelector("#tripist-categories-next-button") as HTMLElement | null;

                        this.chatContainer = this.container.querySelector("#tripist-chat-container") as HTMLElement | null;
                        this.chatSendButton = this.container.querySelector("#tripist-send") as HTMLButtonElement | null;
                        this.chatInputField = this.container.querySelector("#tripist-input") as HTMLInputElement | null;
                        this.chatMessagesContainer = this.container.querySelector("#tripist-messages") as HTMLDivElement | null;
                        this.chatIntroContainer = this.container.querySelector("#tripist-chat-intro-container") as HTMLElement | null;
                        this.chatIntroTitle = this.chatIntroContainer?.querySelector("#tripist-chat-intro-title") as HTMLElement | null;
                        this.chatIntroDescr = this.chatIntroContainer?.querySelector("#tripist-chat-intro-descr") as HTMLElement | null;
                        this.inputContainer = this.container.querySelector("#tripist-input-container") as HTMLElement | null;
                        this.textInput = this.container.querySelector("#tripist-input") as HTMLTextAreaElement | null;
                        this.sendButton = this.container.querySelector("#tripist-send") as HTMLButtonElement | null;
                        this.actionsContainer = this.container.querySelector("#tripist-actions-container") as HTMLElement | null;
                        this.feedbackModal = this.container.querySelector("#tripist-feedback-modal") as HTMLElement | null;
                        this.feedbackStarsContainer = this.container.querySelector(".tripist-feedback-stars") as HTMLElement | null;
                        this.feedbackCommentField = this.container.querySelector("#tripist-feedback-comments") as HTMLTextAreaElement | null;
                        this.feedbackSubmitButton = this.container.querySelector("#tripist-feedback-submit") as HTMLButtonElement | null;

                        this.authContainer = this.container.querySelector("#tripist-auth-container") as HTMLElement | null;
                        this.loginEmail = this.authContainer?.querySelector("#tripist-login-email") as HTMLInputElement | null;
                        this.loginPassword = this.authContainer?.querySelector("#tripist-login-password") as HTMLInputElement | null;
                        this.forgotPasswordLink = this.authContainer?.querySelector("#tripist-forgot-password-link") as HTMLAnchorElement | null;
                        this.loginButton = this.authContainer?.querySelector("#tripist-login-button") as HTMLButtonElement | null;

                        this.registerFirstName = this.authContainer?.querySelector("#tripist-register-first-name") as HTMLInputElement | null;
                        this.registerLastName = this.authContainer?.querySelector("#tripist-register-last-name") as HTMLInputElement | null;
                        this.registerEmail = this.authContainer?.querySelector("#tripist-register-email") as HTMLInputElement | null;
                        this.registerPassword = this.authContainer?.querySelector("#tripist-register-password") as HTMLInputElement | null;
                        this.registerConfirmPassword = this.authContainer?.querySelector("#tripist-register-confirm-password") as HTMLInputElement | null;
                        this.registerLocation = this.authContainer?.querySelector("#tripist-register-location") as HTMLInputElement | null;
                        this.registerButton = this.authContainer?.querySelector("#tripist-register-button") as HTMLButtonElement | null;
                        this.registerCancelButton = this.authContainer?.querySelector("#tripist-register-cancel-button") as HTMLButtonElement | null;

                        this.profileContainer = this.container.querySelector("#tripist-profile-container") as HTMLElement | null;
                        this.profileFirstName = this.profileContainer?.querySelector("#tripist-profile-first-name") as HTMLInputElement | null;
                        this.profileLastName = this.profileContainer?.querySelector("#tripist-profile-last-name") as HTMLInputElement | null;
                        this.profileEmail = this.profileContainer?.querySelector("#tripist-profile-email") as HTMLInputElement | null;
                        this.profileLocation = this.profileContainer?.querySelector("#tripist-profile-location") as HTMLInputElement | null;
                        this.profileAvatarTitle = this.profileContainer?.querySelector(".tripist-avatar-title") as HTMLElement | null;
                        this.profileAvatarSubtitle = this.profileContainer?.querySelector(".tripist-avatar-subtitle") as HTMLElement | null;
                        this.profileAvatarContainer = this.profileContainer?.querySelector("#tripist-avatars") as HTMLElement | null;
                        this.profileButton = this.profileContainer?.querySelector("#tripist-profile-button") as HTMLButtonElement | null;
                        this.profileCancelButton = this.profileContainer?.querySelector("#tripist-profile-cancel-button") as HTMLElement | null;

                        this.exploristResultsContainer = this.container.querySelector("#tripist-explorist-results-container") as HTMLElement | null;
                        this.exploristResultsHeader = this.exploristResultsContainer?.querySelector("#tripist-explorist-results-header") as HTMLElement | null;
                        this.exploristResultsTitle = this.exploristResultsContainer?.querySelector("#tripist-explorist-results-title") as HTMLElement | null;
                        this.exploristResultsMessage = this.exploristResultsContainer?.querySelector("#tripist-explorist-results-message") as HTMLElement | null;
                        this.exploristResultsList = this.exploristResultsContainer?.querySelector("#tripist-explorist-results-list") as HTMLElement | null;

                        // Check for missing elements.
                        const missingElements: string[] = [];
                        if (!this.header) missingElements.push("tripist-header");
                        if (!this.hud) missingElements.push("tripist-hud");
                        if (!this.logoText) missingElements.push("tripist-text");
                        if (!this.menuButton) missingElements.push("tripist-menu-button");
                        if (!this.closeButton) missingElements.push("tripist-close-button");
                        
                        if (!this.hudMessage) missingElements.push("tripist-hud-message");
                        if (!this.hudButton) missingElements.push("tripist-hud-button");

                        if (!this.body) missingElements.push("tripist-body");
                        if (!this.footerContainer) missingElements.push("tripist-footer");

                        if (!this.catContainer) missingElements.push("tripist-categories-container");
                        if (!this.catTitle) missingElements.push("tripist-categories-title");
                        if (!this.catDescr) missingElements.push("tripist-categories-descr");
                        if (!this.catList) missingElements.push("tripist-categories-list");
                        if (!this.catButton) missingElements.push("tripist-categories-next-button");

                        if (!this.chatContainer) missingElements.push("tripist-chat-container");
                        if (!this.chatSendButton) missingElements.push("tripist-send");
                        if (!this.chatInputField) missingElements.push("tripist-input");
                        if (!this.chatMessagesContainer) missingElements.push("tripist-messages");
                        if (!this.chatIntroContainer) missingElements.push("tripist-chat-intro-container");
                        if (!this.chatIntroTitle) missingElements.push("tripist-chat-intro-title");
                        if (!this.chatIntroDescr) missingElements.push("tripist-chat-intro-descr");
                        if (!this.inputContainer) missingElements.push("tripist-input-container");
                        if (!this.textInput) missingElements.push("tripist-input");
                        if (!this.sendButton) missingElements.push("tripist-send");
                        if (!this.actionsContainer) missingElements.push("tripist-actions-container");

                        if (!this.authContainer) missingElements.push("tripist-auth-container");
                        if (!this.loginEmail) missingElements.push("tripist-login-email");
                        if (!this.loginPassword) missingElements.push("tripist-login-password");
                        if (!this.forgotPasswordLink) missingElements.push("tripist-forgot-password-link");
                        if (!this.loginButton) missingElements.push("tripist-login-button");

                        if (!this.registerFirstName) missingElements.push("tripist-register-first-name");
                        if (!this.registerLastName) missingElements.push("tripist-register-last-name");
                        if (!this.registerEmail) missingElements.push("tripist-register-email");
                        if (!this.registerPassword) missingElements.push("tripist-register-password");
                        if (!this.registerConfirmPassword) missingElements.push("tripist-register-confirm-password");
                        if (!this.registerLocation) missingElements.push("tripist-register-location");
                        if (!this.registerButton) missingElements.push("tripist-register-button");
                        if (!this.registerCancelButton) missingElements.push("tripist-register-cancel-button");

                        if (!this.profileContainer) missingElements.push("tripist-profile-container");
                        if (!this.profileFirstName) missingElements.push("tripist-profile-first-name");
                        if (!this.profileLastName) missingElements.push("tripist-profile-last-name");
                        if (!this.profileEmail) missingElements.push("tripist-profile-email");
                        if (!this.profileLocation) missingElements.push("tripist-profile-location");
                        if (!this.profileAvatarTitle) missingElements.push("tripist-avatar-title");
                        if (!this.profileAvatarSubtitle) missingElements.push("tripist-avatar-subtitle");
                        if (!this.profileAvatarContainer) missingElements.push("tripist-avatars");
                        if (!this.profileButton) missingElements.push("tripist-profile-button");
                        if (!this.feedbackModal) missingElements.push("tripist-feedback-modal");
                        if (!this.feedbackStarsContainer) missingElements.push("tripist-feedback-stars");
                        if (!this.feedbackCommentField) missingElements.push("tripist-feedback-comments");
                        if (!this.feedbackSubmitButton) missingElements.push("tripist-feedback-submit");

                        // Only check for rich-result elements if config is for explorist/aura assistant
                        if (this.config && (this.config.integration?.type === "explorist_assistant" || this.config.integration?.type === "aura_assistant")) {
                            if (!this.exploristResultsContainer) missingElements.push("tripist-explorist-results-container");
                            if (!this.exploristResultsHeader) missingElements.push("tripist-explorist-results-header");
                            if (!this.exploristResultsTitle) missingElements.push("tripist-explorist-results-title");
                            if (!this.exploristResultsMessage) missingElements.push("tripist-explorist-results-message");
                            if (!this.exploristResultsList) missingElements.push("tripist-explorist-results-list");
                        }

                        if (missingElements.length > 0) {
                            console.error(`Tripist: Missing required elements in widget HTML: ${missingElements.join(", ")}`);
                            return;
                        }

                        this.initializeFeedbackModal();

                        const docUrl = window.location.href;
                        const base = new URL(this.baseUrl);
                        const doc = new URL(docUrl);
                        if(base.origin === doc.origin){
                            console.log("Tripist: Running in same origin. Setting up full screen mode.");
                            document.body.style.margin = "0";
                            document.body.style.padding = "0";
                            document.body.style.height = "100%";
                            document.body.style.overflow = "hidden";
                            document.documentElement.style.height = "100%";
                            document.querySelector(this.elementSelector)!.style.height = "100%";
                        }

                        const observer = new MutationObserver(() => {
                            this.scrollToEndOfChat();
                        });
                        
                        observer.observe(this.chatMessagesContainer, { childList: true });

                        this.hudButton.addEventListener("click", this.hideHUD.bind(this));

                        this.catButton.addEventListener("click", this.submitCategories.bind(this));

                        this.sendButton.addEventListener("click", this.onSendClicked.bind(this));

                        this.loginButton.addEventListener("click", this.login.bind(this));

                        this.closeButton.addEventListener("click", this.destroy.bind(this));

                        this.forgotPasswordLink.addEventListener("click", this.forgotPassword.bind(this));

                        this.registerButton.addEventListener("click", this.register.bind(this));

                        this.registerCancelButton.addEventListener("click", this.cancelRegistration.bind(this));

                        this.profileButton.addEventListener("click", this.updateProfile.bind(this));
                        if (this.profileCancelButton) {
                            this.profileCancelButton.addEventListener("click", this.cancelProfile.bind(this));
                        }

                        this.chatMessagesContainer.addEventListener('scroll', () => {
                            this.hideAllTooltips();
                        });

                        document.addEventListener('click', (e: MouseEvent) => {
                            if (this.currentlyOpenTooltip) {
                              const target = e.target as Node;
                              // If the click target is neither inside the tooltip nor the associated button, hide the tooltip.
                              if (
                                !this.currentlyOpenTooltip.contains(target) &&
                                (!this.currentlyOpenButton || !this.currentlyOpenButton.contains(target))
                              ) {
                                this.hideAllTooltips();
                              }
                            }
                        });
                        
                        // Override asset paths when using the default template (including fallback)
                        const isDefaultTemplate = !isCustom;
                        console.log(`Is default template: ${isDefaultTemplate}, custom_html: ${this.config.integration.config.custom_html}`);
                        
                        if (isDefaultTemplate) {
                            console.log('Applying default asset paths');
                            this.logoText.src = this.baseUrl + "/assets/default/logo-text.svg";
                            this.menuButton.src = this.baseUrl + "/assets/default/menu.svg";
                            this.closeButton.src = this.baseUrl + "/assets/default/close.svg";
                            this.catButton.querySelector("img")!.src = this.baseUrl + "/assets/default/next.svg";
                        } else {
                            console.log('Using custom template - not overriding asset paths');
                            // For custom templates, the URLs should already be rewritten to absolute URLs
                            // Don't override them as this would break the remote asset loading
                        }
                        
                        this.restoreState();
                        
                        // Mark initialization as complete
                        this.isInitializing = false;

                    })
                    .catch((error) => {
                        console.error("Tripist: Error loading widget template:", error);
                        this.isInitializing = false;
                    });

            } catch (error) {
                console.error("Tripist: Error preparing DOM:", error);
                this.isInitializing = false;
                return;

            }

        },

        initMobile({
            integrationId,
            environment = "production",
            identityToken,
        }: TripistInitMobileParams): void {
            if (!integrationId) {
                console.error("Tripist: integrationId is required.");
                return;
            }

            let env = environment.toLowerCase();
            let baseUrl = baseUrls[env] || baseUrls.production;
            const url = new URL(baseUrl);
            url.searchParams.set('i', integrationId);
            url.searchParams.set('e', environment);
            if (identityToken) {
                url.searchParams.set('it', identityToken);
            }

            // open new window to mobile page
            window.open(url.toString(), "_blank");

        },

        getConfig(): void {

            const shouldBypassTripistCache = this.isTestThread === true || this.disableResultsCache === true;
            const configUrl = shouldBypassTripistCache
                ? `${this.apiBaseUrl}/tripist/${this.integrationId}?bypass_cache=1`
                : `${this.apiBaseUrl}/tripist/${this.integrationId}`;

            fetch(configUrl)
                .then(async (response) => {
                    const payload = await response.json().catch((): null => null);

                    if (!response.ok) {
                        const apiMessage = payload?.error || payload?.message || response.statusText || "Unknown error";
                        throw new Error(`Config request failed (${response.status}): ${apiMessage}`);
                    }

                    if (!payload || typeof payload !== "object") {
                        throw new Error("Config request returned an invalid payload");
                    }

                    if (!payload.integration || !payload.integration.config) {
                        throw new Error("Config payload is missing integration metadata (integration/config)");
                    }

                    this.config = payload;
                    
                    // Set the page title to the integration/app name if provided
                    if (this.config && this.config.integration && this.config.integration.name) {
                        document.title = this.config.integration.name;
                    }

                    // Set widget URLs from config
                    // If custom HTML is specified, use only custom HTML (no default)
                    this.widgetTemplateUrl = this.config.integration.config.custom_html || `${this.baseUrl}/assets/default/widget.html`;

                    // Set custom CSS URL if provided (default CSS will always load first)
                    this.widgetCssUrl = this.config.integration.config.custom_css || `${this.baseUrl}/assets/default/widget.css`;

                    // Load CSS
                    const defaultCssUrl = `${this.baseUrl}/assets/default/widget.css`;
                    
                    console.log(`Default CSS URL: ${defaultCssUrl}`);
                    console.log(`Widget CSS URL: ${this.widgetCssUrl}`);
                    
                    // If custom CSS is provided and different from default, load it as overrides
                    if (this.config.integration.config.custom_css && this.widgetCssUrl !== defaultCssUrl) {
                        console.log('Loading default CSS first, then custom CSS as override');
                        // Load default CSS first, then custom CSS after it's loaded
                        loadCss(defaultCssUrl, "tripist-default-stylesheet", () => {
                            // Use loadRemoteCss for remote custom CSS to fix relative path resolution
                            loadRemoteCss(this.widgetCssUrl, "tripist-custom-stylesheet");
                        });
                    } else {
                        console.log('Loading only default CSS');
                        // Only load default CSS
                        loadCss(defaultCssUrl, "tripist-default-stylesheet");
                    }

                    // Auto-detect identity provider from config response
                    if (this.config.identity_provider && this.config.identity_provider.type) {
                        this.identityProviderType = this.config.identity_provider.type;
                        this.identityProviderConfig = this.config.identity_provider.public_config || {};
                        console.log("Tripist: Detected identity provider:", this.identityProviderType);
                    }

                    try {
                        if (this.identityProviderType === 'identity_firebase') {
                            let fbConfig = this.identityProviderConfig.firebase_config;
                            if (!fbConfig && this.config.integration.config.firebase_config) {
                                fbConfig = this.config.integration.config.firebase_config;
                            }
                            if (fbConfig) {
                                this.config.integration.config.firebase_config = fbConfig;
                            }
                            this.initFirebase();
                        } else if (this.identityProviderType) {
                            this.initExternalProvider();
                        } else if (this.config.integration.config.firebase_config) {
                            console.warn("Tripist: Using legacy Firebase flow (no identity_provider in config)");
                            this.identityProviderType = null;
                            this.initFirebase();
                        } else {
                            throw new Error(
                                "No identity provider is configured for this integration. " +
                                "Please assign an identity provider (Firebase, HMAC, or Simulator) in the Lighthouse Admin UI under Integrations."
                            );
                        }
                    } catch (providerError: any) {
                        const providerLabel = this.identityProviderType || "unknown";
                        const message =
                            `Tripist could not start: failed to initialize the "${providerLabel}" identity provider. ` +
                            providerError.message;
                        console.error("Tripist:", message);
                        this.isInitializing = false;
                        if (this.container) {
                            this.container.innerHTML =
                                `<div style="padding:24px;color:#e74c3c;font-family:sans-serif;font-size:14px;text-align:center;">${message}</div>`;
                        }
                    }

                })
                .catch((error) => {
                    const message = `Tripist could not start: ${error.message || error}`;
                    console.error("Tripist:", message);
                    this.isInitializing = false;
                    if (this.container) {
                        this.container.innerHTML =
                            `<div style="padding:24px;color:#e74c3c;font-family:sans-serif;font-size:14px;text-align:center;">${message}</div>`;
                    }
                });
        },

        async initExternalProvider(): Promise<void> {
            let storedDeviceId = this.getLocalStorage("device_identifier");
            if (!storedDeviceId) {
                storedDeviceId = crypto.randomUUID();
                this.setLocalStorage("device_identifier", storedDeviceId);
            }
            this.deviceIdentifier = storedDeviceId;

            const browserInfo = await this.getBrowserInfo();
            let authResponse = await fetch(`${this.apiBaseUrl}/auth/device`, {
                method: "POST",
                body: JSON.stringify({
                    api_id: this.config.integration.api_id,
                    api_key: this.config.integration.api_key,
                    device_identifier: this.deviceIdentifier,
                    make: 'web',
                    model: this.getBrowserModel(browserInfo),
                    system_name: browserInfo.os,
                    system_version: browserInfo.osVersion,
                    app_version: '1.0.0',
                }),
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (!authResponse.ok) {
                const body = await authResponse.json().catch(() => ({}));
                throw new Error(
                    `Device registration failed (${authResponse.status}): ${body.message || authResponse.statusText}`
                );
            }

            this.lhpeDevice = await authResponse.json();

            const authHeader = authResponse.headers.get("Authorization");
            if (authHeader) {
                this.setLocalStorage("auth_token", authHeader);
            }

            this.prepareDOM();

            if (this.onReady) this.onReady();
        },

        initFirebase(): void {

            if (!this.config.integration.config.firebase_config) {
                throw new Error(
                    "Firebase config is missing. The integration is configured to use Firebase authentication " +
                    "but no firebase_config was found. Check the identity provider configuration in the Admin UI."
                );
            }

            const fbConfig = this.config.integration.config.firebase_config;
            if (!fbConfig.projectId && !fbConfig.apiKey) {
                throw new Error(
                    "Firebase config is invalid (missing projectId and apiKey). " +
                    "Verify the Firebase web app configuration in the identity provider integration."
                );
            }

            try {
                this.firebaseApp = initializeApp(fbConfig);
                console.log("Tripist: Firebase app initialized");
            } catch (error: any) {
                if (error.code === 'app/duplicate-app') {
                    console.log("Tripist: Firebase app already exists, using existing instance");
                    this.firebaseApp = getApp();
                } else {
                    throw new Error(`Firebase SDK initialization failed: ${error.message}`);
                }
            }

            try {
                this.firebaseAnalytics = getAnalytics(this.firebaseApp);
            } catch (error: any) {
                console.warn("Tripist: Firebase Analytics could not be initialized:", error.message);
            }

            try {
                this.firebaseAuth = getAuth();
            } catch (error: any) {
                throw new Error(
                    `Firebase Auth could not be initialized. This usually means the Firebase config is incomplete ` +
                    `(e.g., missing projectId). Verify the Firebase web app configuration. Detail: ${error.message}`
                );
            }

                // Store the unsubscribe function so we can clean up later
                this.firebaseAuthUnsubscribe = onAuthStateChanged(this.firebaseAuth, async (user) => {
                    if (user) {
                        console.log("Tripist: onAuthStateChanged: User signed in. Anonymous? ", user.isAnonymous);
                        this.firebaseUser = user;
                        this.deviceIdentifier = user.uid;

                        if (this.identityProviderType === 'identity_firebase') {
                            // New unified path: call identify() which verifies via the API
                            await this.identify();
                        } else {
                            // Legacy fallback: call initLHPE directly
                            await this.initLHPE();
                        }
                        
                    } else {
                        console.log("Tripist: onAuthStateChanged: User is null");
                        try {
                            console.log("Tripist: Authenticating anonymously...");
                            await signInAnonymously(this.firebaseAuth);
                        } catch (error) {
                            console.error("Tripist: Error signing in anonymously:", error);
                        }

                    }

                });
        },

        async initLHPE(): Promise<void> {

            const browserInfo = await this.getBrowserInfo();

            let authResponse = await fetch(`${this.apiBaseUrl}/auth/device`, {
                method: "POST",
                body: JSON.stringify({
                    api_id: this.config.integration.api_id,
                    api_key: this.config.integration.api_key,
                    device_identifier: this.firebaseUser.uid,
                    make: 'web',
                    model: this.getBrowserModel(browserInfo),
                    system_name: browserInfo.os,
                    system_version: browserInfo.osVersion,
                    app_version: '1.0.0',
                }),
                headers: {
                    "Content-Type": "application/json",
                },
            });

            this.lhpeDevice = await authResponse.json();

            if (this.lhpeDevice.appUser) {
                this.lhpeUser = this.lhpeDevice.appUser;
            }

            this.isIdentified = !!(this.firebaseUser && !this.firebaseUser.isAnonymous && this.lhpeUser);
            this.deviceIdentifier = this.firebaseUser.uid;

            const authHeader = authResponse.headers.get("Authorization");
            if (authHeader) {
                this.setLocalStorage("auth_token", authHeader);
            } else {
                console.error("Tripist: No Authorization header received");
            }

            let data = await this.getUserProfileData();

            // Restore selected categories
            if (data && data.selectedCategories) {
                this.selectedCategories = data.selectedCategories.filter((selectedId: number) => this.config.categories.some((cat: any) => cat.category_id === selectedId));   
            }

            if (this.isIdentified && this.lhpeUser && this.thread) {
                await this.updateThreadWithUserId();
            }

            this.prepareDOM();

            if (this.chatState.messages.length > 0) {
                this.buildChatHistory();
            }

            if (this.onReady) this.onReady();
        },

        async updateThreadWithUserId(): Promise<void> {
            if (!this.lhpeUser || !this.thread) {
                console.log("Tripist: Cannot update thread - missing user or thread");
                return;
            }

            const userId = this.lhpeUser.id || this.lhpeUser.user_id || this.lhpeUser.userId;
            if (!userId) {
                console.log("Tripist: Cannot update thread - no user ID found");
                return;
            }

            try {
                console.log(`Tripist: Updating thread ${this.thread} with user ID ${userId}`);
                
                const response = await fetch(`${this.apiBaseUrl}/tripist/${this.integrationId}/thread/${this.thread}`, {
                    method: "PATCH",
                    body: JSON.stringify({
                        account_app_user_id: userId
                    }),
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": this.getLocalStorage("auth_token") || "",
                    },
                });

                if (response.ok) {
                    const result = await response.json();
                    console.log("Tripist: Thread updated successfully with user ID", result);
                } else {
                    console.error(`Tripist: Failed to update thread with user ID. Status: ${response.status}`);
                }
            } catch (error) {
                console.error("Tripist: Error updating thread with user ID:", error);
            }
        },

        async identify(identity?: ExternalIdentity): Promise<void> {
            const browserInfo = await this.getBrowserInfo();
            const payload: any = {
                api_id: this.config.integration.api_id,
                api_key: this.config.integration.api_key,
                device_identifier: this.deviceIdentifier,
                make: 'web',
                model: this.getBrowserModel(browserInfo),
                system_name: browserInfo.os,
                system_version: browserInfo.osVersion,
            };

            if (this.identityProviderType === 'identity_firebase') {
                payload.firebase_id_token = await this.firebaseUser.getIdToken(true);
            } else if (identity) {
                payload.external_user_id = identity.externalUserId;
                payload.hash = identity.hash;
                payload.email = identity.email;
                payload.first_name = identity.firstName;
                payload.last_name = identity.lastName;
                payload.profile = identity.profile;
            }

            try {
                const response = await fetch(`${this.apiBaseUrl}/auth/identify`, {
                    method: "POST",
                    body: JSON.stringify(payload),
                    headers: { "Content-Type": "application/json" }
                });

                if (!response.ok) {
                    const error = await response.json();
                    if (this.onIdentifyError) this.onIdentifyError(error);
                    throw new Error(error.message);
                }

                const data = await response.json();
                this.lhpeDevice = data;
                if (data.appUser) this.lhpeUser = data.appUser;
                this.isIdentified = !!this.lhpeUser;

                const authHeader = response.headers.get("Authorization");
                if (authHeader) this.setLocalStorage("auth_token", authHeader);

                if (this.isIdentified && this.thread) {
                    await this.updateThreadWithUserId();
                }

                // For Firebase provider, we need to initialize the DOM on first call
                if (this.identityProviderType === 'identity_firebase' && !this.container?.innerHTML) {
                    let profileData = await this.getUserProfileData();
                    if (profileData && profileData.selectedCategories) {
                        this.selectedCategories = profileData.selectedCategories.filter(
                            (selectedId: number) => this.config.categories.some((cat: any) => cat.category_id === selectedId)
                        );
                    }
                    this.prepareDOM();
                    if (this.chatState && this.chatState.messages.length > 0) {
                        this.buildChatHistory();
                    }
                    if (this.onReady) this.onReady();
                } else {
                    this.setupMenuTooltips();
                }

                if (this.onIdentify) this.onIdentify(this.lhpeUser);

            } catch (error: any) {
                console.error("Tripist: identify() error:", error);
                if (this.onIdentifyError) this.onIdentifyError(error);
            }
        },

        async clearIdentity(): Promise<void> {
            if (this.identityProviderType === 'identity_firebase') {
                await signOut(this.firebaseAuth);
            } else {
                this.lhpeUser = null;
                this.isIdentified = false;
                this.removeLocalStorage("auth_token");

                const browserInfo = await this.getBrowserInfo();
                const authResponse = await fetch(`${this.apiBaseUrl}/auth/device`, {
                    method: "POST",
                    body: JSON.stringify({
                        api_id: this.config.integration.api_id,
                        api_key: this.config.integration.api_key,
                        device_identifier: this.deviceIdentifier,
                        make: 'web',
                        model: this.getBrowserModel(browserInfo),
                        system_name: browserInfo.os,
                        system_version: browserInfo.osVersion,
                    }),
                    headers: { "Content-Type": "application/json" }
                });

                this.lhpeDevice = await authResponse.json();
                const authHeader = authResponse.headers.get("Authorization");
                if (authHeader) this.setLocalStorage("auth_token", authHeader);

                this.setupMenuTooltips();
            }
        },

        get isAnonymous(): boolean {
            return !this.isIdentified;
        },

        async recordThreadShare(): Promise<number | null> {
            if (!this.thread) {
                console.log("Tripist: Cannot record share - no active thread");
                return null;
            }

            try {
                console.log(`Tripist: Recording share for thread ${this.thread}`);
                
                const response = await fetch(`${this.apiBaseUrl}/tripist/${this.integrationId}/thread/${this.thread}/share`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                });

                if (response.ok) {
                    const result = await response.json();
                    console.log(`Tripist: Thread share recorded successfully. Total shares: ${result.shares}`, result);
                    return result.shares;
                } else {
                    console.error(`Tripist: Failed to record thread share. Status: ${response.status}`);
                    return null;
                }
            } catch (error) {
                console.error("Tripist: Error recording thread share:", error);
                return null;
            }
        },

        makeMenuItem(name: string, labelText: string): HTMLElement {
            let row = document.createElement("div");
            row.classList.add("tripist-menu-row");
            row.style.whiteSpace = "nowrap"; // prevent text wrapping

            let iconContainer = document.createElement("div");
            iconContainer.classList.add("tripist-menu-icon");

            let icon = document.createElement("img");
            icon.classList.add("tripist-menu-icon");
            icon.classList.add("tripist-" + name + "-icon");

            let label = document.createElement("div");
            label.classList.add("tripist-menu-label");
            label.classList.add("tripist-" + name + "-label");
            label.textContent = labelText;
            label.style.whiteSpace = "nowrap"; // prevent text wrapping

            iconContainer.appendChild(icon);
            row.appendChild(iconContainer);
            row.appendChild(label);

            return row;
        },

        ensureFeedbackModalElements(): void {
            if (!this.container) {
                return;
            }

            let feedbackModal = this.container.querySelector("#tripist-feedback-modal") as HTMLElement | null;
            if (!feedbackModal) {
                feedbackModal = document.createElement("div");
                feedbackModal.id = "tripist-feedback-modal";
                feedbackModal.setAttribute("aria-hidden", "true");
                this.container.appendChild(feedbackModal);
            }

            let content = feedbackModal.querySelector(".tripist-feedback-modal-content");
            if (!content) {
                content = document.createElement("div");
                content.className = "tripist-feedback-modal-content";

                const stars = document.createElement("div");
                stars.className = "tripist-feedback-stars";

                const textarea = document.createElement("textarea");
                textarea.id = "tripist-feedback-comments";
                textarea.placeholder = "Add comments here";

                const button = document.createElement("button");
                button.id = "tripist-feedback-submit";
                button.textContent = "Submit Feedback";

                content.appendChild(stars);
                content.appendChild(textarea);
                content.appendChild(button);
                feedbackModal.appendChild(content);
            }
        },

        setMenuAnonymous(): void {
            if(this.menuButton === null){
                return;
            }

            const self = this;
            
            let menuCol = document.createElement("div");
            menuCol.classList.add("tripist-menu-container");

            const isFirebaseProvider = this.identityProviderType === 'identity_firebase' || (!this.identityProviderType && this.firebaseAuth);
            let signIn: HTMLElement | null = null;
            if (isFirebaseProvider) {
                signIn = this.makeMenuItem("login", "Login or Register");
                menuCol.appendChild(signIn);
            }
            
            let restart = null;
            if(this.config && (this.config.integration?.type === "explorist_assistant" || this.config.integration?.type === "aura_assistant") || this.currentState === AppState.Chat){
                restart = this.makeMenuItem("restart", "Start New Chat");
                menuCol.appendChild(restart);
            }

            if (!menuCol.hasChildNodes()) {
                this.menuButton.style.display = "none";
                return;
            }
            this.menuButton.style.display = "";

            this.attachTooltip(this.menuButton, menuCol, function(){

                if (signIn) {
                    signIn.addEventListener("click", (e: any) => {
                        self.setState(AppState.Auth);
                        self.hideAllTooltips();
                    });
                }
    
                if(restart){
                    restart.addEventListener("click", (e: any) => {
                        self.reset();
                        self.hideAllTooltips();
                    });
                }
            });

        },

        setMenuAuthenticated(): void {
            if(this.menuButton === null){
                return;
            }
            
            const self = this;
            
            let menuCol = document.createElement("div");
            menuCol.classList.add("tripist-menu-container");

            const isFirebaseProvider = this.identityProviderType === 'identity_firebase' || (!this.identityProviderType && this.firebaseAuth);

            let profile: HTMLElement | null = null;
            if (isFirebaseProvider) {
                profile = this.makeMenuItem("profile", "Edit Profile");
                menuCol.appendChild(profile);
            }
            
            let restart = null;
            if(this.config && (this.config.integration?.type === "explorist_assistant" || this.config.integration?.type === "aura_assistant") || this.currentState === AppState.Chat){
                restart = this.makeMenuItem("restart", "Start New Chat");
                menuCol.appendChild(restart);
            }

            let logout: HTMLElement | null = null;
            if (isFirebaseProvider) {
                logout = this.makeMenuItem("logout", "Log Out");
                menuCol.appendChild(logout);
            }

            if (!menuCol.hasChildNodes()) {
                this.menuButton.style.display = "none";
                return;
            }
            this.menuButton.style.display = "";

            this.attachTooltip(this.menuButton, menuCol, function(){

                if (profile) {
                    profile.addEventListener("click", (e: any) => {
                        self.setState(AppState.Profile);
                        self.hideAllTooltips();
                    });
                }
                
                if (logout) {
                    logout.addEventListener("click", (e: any) => {
                        self.clearIdentity().then(() => {
                            self.selectedCategories = [];
                            self.lhpeUser = null;
                            self.isIdentified = false;
                            self.removeLocalStorage("auth_token");
                            self.reset();
                            self.setState(AppState.Categories);
                            self.hideAllTooltips();
                        }).catch((error: any) => {
                            console.error("Tripist: Error signing out:", error);
                        });
                    });
                }
    
                if(restart){
                    restart.addEventListener("click", (e: any) => {
                        self.reset();
                        self.hideAllTooltips();
                    });
                }
            });

        },

        restoreState(): void {
            if (this.thread) {
                this.setState(AppState.Chat);
            } else {
                this.setState(AppState.Categories);
            }
        },

        isFirebaseProvider(): boolean {
            return this.identityProviderType === 'identity_firebase' || (!this.identityProviderType && !!this.firebaseAuth);
        },

        async getUserProfileData(): Promise<any> {
            if (!this.isIdentified && this.lhpeDevice) {
                return this.lhpeDevice.temp_profile;
            } else if (this.lhpeUser && this.lhpeUser.profile) {
                return this.lhpeUser.profile;
            }
        },

        async updateUserData(data: any): Promise<void> {
            
            let baseData = {
                selectedCategories: this.selectedCategories,
                ...data
            }

            if(!this.isIdentified && this.lhpeDevice && this.lhpeDevice.temp_profile){
                baseData = {
                    ...this.lhpeDevice.temp_profile,
                    ...baseData
                }
            } else if(this.lhpeUser){
                const existingUserData = {
                    first_name: this.lhpeUser.first_name,
                    last_name: this.lhpeUser.last_name,
                    email: this.lhpeUser.email,
                    ...(this.lhpeUser.profile || {})
                };
                baseData = {
                    ...existingUserData,
                    ...baseData
                };
                console.log("Merged existing LHPE user data:", existingUserData);
            }

            if (!this.isIdentified) {
                console.log("Tripist: Updating anonymous user data", baseData);
                
                let deviceReponse = await fetch(`${this.apiBaseUrl}/app/device`, {
                    method: "PUT",
                    body: JSON.stringify({
                        temp_profile: baseData,
                    }),
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": this.getLocalStorage("auth_token") || "",
                    },
                });
                let deviceData = await deviceReponse.json();
                
                if (deviceData.device) {
                    this.lhpeDevice = deviceData.device;
                } else if (deviceData.temp_profile && this.lhpeDevice) {
                    this.lhpeDevice.temp_profile = deviceData.temp_profile;
                }
                return;
            } 
            else {
                console.log("Tripist: Updating user data", baseData);
                
                const foreignId = this.firebaseUser ? this.firebaseUser.uid : (this.lhpeUser?.foreign_id || this.deviceIdentifier);
                const userFields = {
                    foreign_id: foreignId,
                    first_name: baseData.first_name,
                    last_name: baseData.last_name,
                    email: baseData.email || (this.firebaseUser ? this.firebaseUser.email : this.lhpeUser?.email),
                };
                
                const profileData = { ...baseData };
                delete profileData.first_name;
                delete profileData.last_name;
                delete profileData.email;
                
                const payload = {
                    ...userFields,
                    profile: profileData,
                };
                
                console.log("Tripist: Sending user payload:", payload);
                
                let userResponse = await fetch(`${this.apiBaseUrl}/app/user`, {
                    method: "POST",
                    body: JSON.stringify(payload),
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": this.getLocalStorage("auth_token") || "",
                    },
                });
                let userData = await userResponse.json();
                // /app/user returns the serialized user object at the top level.
                // Keep a fallback for legacy wrapped responses.
                this.lhpeUser = userData?.user || userData;
                return;
            }
        },

        getAvatarUrl() {
            const avatars = this.config.integration.config.avatars;
            const defaultAvatar = avatars?.length > 0 ? avatars[0].img : `${this.baseUrl}/assets/default/icon.svg`;

            if (!this.lhpeUser?.profile) {
                return defaultAvatar;
            }
            const avatarId = this.lhpeUser.profile.avatar_id;
            if (!avatarId || !avatars?.length) {
                return defaultAvatar;
            }
            return avatars.find((a: any) => a.id === avatarId)?.img || defaultAvatar;
        },

        reset(): void {
            this.thread = null;
            this.removeLocalStorage("thread_id");
            this.removeLocalStorage("results");
            this.removeLocalStorage("results_share");
            this.downloadUrl = null;
            this.shareDownloadUrl = null;
            this.exploristResultsData = null;
            
            // Only reset DOM elements if they exist (widget might have been destroyed)
            if (this.chatState) {
                this.chatState.messages = [];
            }
            if (this.chatMessagesContainer) {
                this.chatMessagesContainer.innerHTML = "";
            }
            if (this.actionsContainer) {
                this.actionsContainer.innerHTML = "";
            }
            
            this.currentTempOutboundId = null;
            this.currentTempInboundId = null;
            this.currentMessageHasToolCalls = false;
            
            if (this.body) {
                this.body.scrollTop = 0;
            }
            
            // Only set state if widget is still initialized
            if (this.container && this.container.innerHTML) {
                this.setState(AppState.Categories);
            }
        },

        setState(newState: AppState): void {

            console.log("Tripist: Setting state to: ", newState);

            this.showHUD();
            this.previousState = this.currentState;
            this.currentState = newState;
            
            // Render and wait for completion before setting up tooltips
            const renderResult = this.render();
            
            // Handle both sync and async render operations
            if (renderResult && typeof renderResult.then === 'function') {
                // Async render - wait for completion
                renderResult.then(() => {
                    this.setupMenuTooltips();
                    this.hideHUD();
                }).catch((error: any) => {
                    console.error("Tripist: Error in async render:", error);
                    this.setupMenuTooltips();
                    this.hideHUD();
                });
            } else {
                // Sync render - setup immediately
                this.setupMenuTooltips();
                this.hideHUD();
            }
        },

        setupMenuTooltips(): void {
            if (this.currentState === AppState.Profile) {
                if (this.menuButton) {
                    this.menuButton.style.display = "none";
                }
                if (this.closeButton) {
                    this.closeButton.style.display = "none";
                }
                return;
            }

            if (this.closeButton) {
                this.closeButton.style.display = "";
            }

            if (this.isIdentified) {
                this.setMenuAuthenticated();
            } else if (this.firebaseUser && !this.firebaseUser.isAnonymous) {
                this.setMenuAuthenticated();
            } else {
                this.setMenuAnonymous();
            }
        },

        render(): Promise<void> | void {
            console.log("Tripist: Rendering state: ", this.currentState);

            this.catContainer.style.display = "none";
            this.chatContainer.style.display = "none";
            this.footerContainer.style.display = "none";
            this.authContainer.style.display = "none";
            this.profileContainer.style.display = "none";
            this.exploristResultsContainer.style.display = "none";

            // Hide body for explorist results, show for all other states
            if (this.currentState === AppState.ExploristResults) {
                this.body.style.display = "none";
            } else {
                this.body.style.display = "flex";
            }

            switch (this.currentState) {
                case AppState.Default:
                    console.log("Tripist: Default state");
                break;
                case AppState.Categories:
                    this.renderCategories();
                break;
                case AppState.Chat:
                    return this.renderChat(); // Return the promise for async operation
                case AppState.Results:
                    this.renderResults();
                break;
                case AppState.ExploristResults:
                    this.renderExploristResults();
                break;
                case AppState.Auth:
                    this.renderAuth();
                    this.initPlaces();
                break;
                case AppState.Profile:
                    this.renderProfile();
                    this.initPlaces();
                break;
                default:
                    console.error("Tripist: Unhandled state for render: ", this.currentState);
                break;
            }
        },

        renderCategories(): void {

            // Prepare Categories
            this.catList.innerHTML = "";

            this.catTitle.textContent = this.config.integration.config.category_picker_title;
            this.catDescr.textContent = this.config.integration.config.category_picker_description;
            
            this.config.categories.forEach((cat: any) => {
                const catItem = document.createElement("div");
                catItem.classList.add("tripist-category");
                catItem.dataset.categoryId = cat.category_id;

                // If category is selected, add selected class
                if (this.selectedCategories.includes(cat.category_id)) {
                    catItem.classList.add("tripist-category-selected");
                }

                const catIconContainer = document.createElement("div");
                catIconContainer.classList.add("tripist-category-icon-container");

                if (cat.image_url) {
                    const catIcon = document.createElement("img");
                    catIcon.src = cat.image_url;
                    catIconContainer.appendChild(catIcon);
                } else {
                    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
                    svg.setAttribute("viewBox", "0 0 64 64");
                    svg.setAttribute("width", "56");
                    svg.setAttribute("height", "56");
                    svg.style.margin = "2px";
                    svg.innerHTML =
                        '<path d="M32 4L4 20v24l28 16 28-16V20L32 4z" fill="none" stroke="#bbb" stroke-width="2"/>' +
                        '<path d="M32 4L4 20l28 16 28-16L32 4z" fill="#e8e8e8" stroke="#bbb" stroke-width="2"/>' +
                        '<path d="M4 20v24l28 16V36L4 20z" fill="#d5d5d5" stroke="#bbb" stroke-width="2"/>' +
                        '<path d="M60 20v24L32 60V36l28-16z" fill="#ddd" stroke="#bbb" stroke-width="2"/>';
                    catIconContainer.appendChild(svg);
                }

                catItem.appendChild(catIconContainer);

                const catName = document.createElement("div");
                catName.classList.add("tripist-category-name");
                catName.textContent = cat.category_name;
                catItem.appendChild(catName);

                const catCheck = document.createElement("div");
                catCheck.classList.add("tripist-category-check");
                catItem.appendChild(catCheck);

                catItem.addEventListener("click", this.categoryClicked.bind(this, cat.category_id));

                this.catList.appendChild(catItem);
            });

            this.catContainer.style.display = "flex";
        },

        renderAuth(): void {
            this.authContainer.style.display = "flex";
        },

        renderProfile(): void {
            this.profileFirstName.value = this.lhpeUser?.first_name || "";
            this.profileLastName.value = this.lhpeUser?.last_name || "";
            this.profileEmail.value = this.lhpeUser?.email || (this.firebaseUser ? this.firebaseUser.email : "") || "";
            this.profileLocation.value = this.lhpeUser?.profile?.location || "";
            this.profileContainer.style.display = "flex";

            this.profileAvatarContainer.innerHTML = "";
            const avatars = this.config.integration.config.avatars || [];
            avatars.forEach((avatar: any) => {
                const avatarItem = document.createElement("div");
                avatarItem.classList.add("tripist-avatar-item");
                avatarItem.dataset.avatarId = avatar.id;

                const avatarIcon = document.createElement("img");
                avatarIcon.src = avatar.img;
                avatarItem.appendChild(avatarIcon);

                // Check if this avatar is currently selected (handle null lhpeUser case)
                const currentAvatarId = this.lhpeUser?.profile?.avatar_id;
                if (currentAvatarId && currentAvatarId === avatar.id) {
                    avatarItem.classList.add("tripist-avatar-selected");
                } else if (!currentAvatarId && avatars.length > 0 && avatar.id === avatars[0].id) {
                    // If no avatar is selected and lhpeUser is null, default to first avatar
                    avatarItem.classList.add("tripist-avatar-selected");
                }

                avatarItem.addEventListener("click", () => {
                    this.profileAvatarContainer.querySelectorAll(".tripist-avatar-item").forEach((item: any) => {
                        item.classList.remove("tripist-avatar-selected");
                    });
                    avatarItem.classList.add("tripist-avatar-selected");
                });

                this.profileAvatarContainer.appendChild(avatarItem);
            });

        },

        async renderChat(): Promise<void> {

            this.chatIntroTitle.textContent = this.config.integration.config.chat_intro_title;
            this.chatIntroDescr.textContent = this.config.integration.config.chat_intro_description;
            this.chatInputField.placeholder = this.config.integration.config.chat_input_placeholder;

            // Init thread if not already
            if(this.thread === null) {
                // New thread - show footer immediately
                this.footerContainer.style.display = "flex";

                // Prepare thread initialization data
                let threadInitData: any = {};
                
                // Add device ID if available
                if (this.lhpeDevice) {
                    const deviceId = this.lhpeDevice.id || this.lhpeDevice.device_id || this.lhpeDevice.deviceId;
                    if (deviceId) {
                        threadInitData.lhpeDeviceId = deviceId;
                    }
                }
                
                // Add user ID if authenticated and available
                if (this.isIdentified && this.lhpeUser) {
                    const userId = this.lhpeUser.id || this.lhpeUser.user_id || this.lhpeUser.userId;
                    if (userId) {
                        threadInitData.lhpeUserId = userId;
                    }
                }
                if (this.isTestThread) {
                    threadInitData.isTestThread = true;
                }

                const threadInitHeaders: Record<string, string> = {
                    "Content-Type": "application/json",
                    ...this.getClientContextHeaders()
                };
                const authToken = this.getLocalStorage("auth_token");
                if (authToken) {
                    threadInitHeaders.Authorization = authToken;
                }

                const threadInitResponse = await fetch(`${this.apiBaseUrl}/tripist/${this.integrationId}/thread`, {
                    method: "POST",
                    body: JSON.stringify(threadInitData),
                    headers: threadInitHeaders
                });

                const initResponse = await threadInitResponse.json();
                if (this.currentState !== AppState.Chat) {
                    return;
                }
                this.setLocalStorage("thread_id", initResponse.thread_id);
                this.thread = initResponse.thread_id;

                this.chatIntroContainer.style.display = "flex";

                // Send Intro Message
                this.sendMessage(null);

            } else {
                // Get Thread History
                const threadHistoryResponse = await fetch(`${this.apiBaseUrl}/tripist/${this.integrationId}/thread/${this.thread}`, {
                    headers: {
                        "Content-Type": "application/json",
                        ...this.getClientContextHeaders()
                    }
                });

                let threadHistory = await threadHistoryResponse.json();
                if (this.currentState !== AppState.Chat) {
                    return;
                }

                // rebuild chat history
                this.chatState.messages = threadHistory;
                this.reconcileOptionToolCallsFromSelectionMetadata();

                // Check if there are explorist results in history before showing footer
                const hasExploristResults = threadHistory.some((message: any) => 
                    message.tool_calls && message.tool_calls.some((toolCall: any) => 
                        (
                            toolCall.function.name.trim() === "final_results" ||
                            toolCall.function.name.trim() === "get_category_results"
                        ) &&
                        this.config && this.config.integration?.type === "explorist_assistant"
                    )
                );

                // Only show footer if no explorist results in history
                if (!hasExploristResults) {
                    this.footerContainer.style.display = "flex";
                }

                // Reevaluate tool calls to show action buttons
                await this.updateMessageTools();

                if(threadHistory.length > 2){
                    this.chatIntroContainer.style.display = "none";
                }

            }

            if (this.currentState !== AppState.Chat) {
                return;
            }

            this.chatContainer.style.display = "flex";
            this.inputContainer.style.display = "flex";
            this.actionsContainer.style.display = "none";
            
        },

        renderResults(): void {

            this.chatContainer.style.display = "flex";
            this.inputContainer.style.display = "none";
            this.actionsContainer.style.display = "flex";
            this.footerContainer.style.display = "flex";

        },

        renderExploristResults(): void {

            this.exploristResultsContainer.style.display = "flex";
            this.footerContainer.style.display = "none";
            this.inputContainer.style.display = "none";
            this.actionsContainer.style.display = "none";

        },

        categoryClicked(category_id: number): void {
            const catItem = this.catContainer.querySelector(`.tripist-category[data-category-id="${category_id}"]`) as HTMLDivElement | null;
            if (this.selectedCategories.includes(category_id)) {
                this.selectedCategories = this.selectedCategories.filter((selectedId: number) => selectedId !== category_id);
                if (catItem) {
                    catItem.classList.remove("tripist-category-selected");
                }
            } else {
                this.selectedCategories.push(category_id);
                if (catItem) {
                    catItem.classList.add("tripist-category-selected");
                }
            }
        },

        submitCategories(): void {
            if (this.selectedCategories.length == 0) {
                this.showHUD("Please select at least one category.", true);
                return;
            }
            this.showHUD();
            this.updateUserData({
                selectedCategories: this.selectedCategories
            }).then(() => {
                this.setState(AppState.Chat);
            }).catch((error: any) => {
                console.error("Tripist: Error updating user data:", error);
            });
            
        },

        login(): void {
            // Ensure all fields are filled
            if (!this.loginEmail.value || !this.loginPassword.value) {
                this.showHUD("Please fill in all fields.", true);
                return;
            }
            const auth = getAuth();
            signInWithEmailAndPassword(auth, this.loginEmail.value, this.loginPassword.value)
            .then(async (userCredential) => {
                console.log("Tripist: User logged in", userCredential);
                
                // Update thread with user ID if there's an active thread
                // Note: initLHPE will be called by onAuthStateChanged, but we need to ensure
                // the thread is updated after the user data is properly loaded
                const waitForLHPEUser = () => {
                    return new Promise<void>((resolve) => {
                        const checkLHPEUser = () => {
                            if (this.lhpeUser && this.thread) {
                                this.updateThreadWithUserId().then(() => {
                                    console.log("Tripist: Thread updated after login");
                                    resolve();
                                }).catch((error: any) => {
                                    console.error("Tripist: Error updating thread after login:", error);
                                    resolve();
                                });
                            } else if (this.lhpeUser || this.thread) {
                                // Give it a moment for both to be set
                                setTimeout(checkLHPEUser, 100);
                            } else {
                                resolve();
                            }
                        };
                        checkLHPEUser();
                    });
                };
                
                // Wait a moment for initLHPE to complete, then update thread
                setTimeout(() => {
                    waitForLHPEUser();
                }, 500);
            })
            .catch((error) => {
                const errorCode = error.code;
                const errorMessage = error.message;
                console.error("Tripist: Error logging in:", errorCode, errorMessage);
                if(errorCode === 'auth/invalid-credential'){
                    this.showHUD("Invalid credentials. Please try again.", true);
                }
            });
            
        },

        forgotPassword(): void {
            // Ensure all fields are filled
            if (!this.loginEmail.value) {
                this.showHUD("Please fill in the email field.", true);
                return;
            }
            const auth = getAuth();
            sendPasswordResetEmail(auth, this.loginEmail.value)
            .then(() => {
                // Password reset email sent!
                console.log("Tripist: Password reset email sent.");
                this.showHUD("Password reset email sent.", true);
            })
            .catch((error) => {
                const errorCode = error.code;
                const errorMessage = error.message;
                console.error("Tripist: Error sending password reset email:", errorCode, errorMessage);
            });
        },

        loadGoogleMapsAPI(): Promise<void> {
            return new Promise((resolve, reject) => {
              console.log("Loading Google Maps API...");
              
              // If the API is already loaded, resolve immediately.
              if (typeof google !== 'undefined' && google.maps && google.maps.places) {
                console.log("Google Maps API already loaded");
                resolve();
                return;
              }

              // Check if script is already being loaded
              const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
              if (existingScript) {
                console.log("Google Maps script already exists, waiting for load...");
                // Wait for existing script to load
                existingScript.addEventListener('load', () => {
                  if (typeof google !== 'undefined' && google.maps && google.maps.places) {
                    console.log("Google Maps API loaded via existing script");
                    resolve();
                  } else {
                    reject(new Error("Google Maps API loaded but objects not available"));
                  }
                });
                existingScript.addEventListener('error', () => {
                  reject(new Error("Failed to load Google Maps API via existing script"));
                });
                return;
              }
          
              const script = document.createElement('script');
              script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyA4bikd6SOJFgo4Os03XFVgoaftFG6N1Kw&libraries=places`;
              script.async = true;
              script.defer = true;
          
              script.onload = () => {
                console.log("Google Maps script loaded");
                // Add a small delay to ensure all objects are available
                setTimeout(() => {
                  if (typeof google !== 'undefined' && google.maps && google.maps.places) {
                    console.log("Google Maps API ready");
                    resolve();
                  } else {
                    console.error("Google Maps API loaded but objects not available");
                    reject(new Error("Google Maps API loaded but objects not available"));
                  }
                }, 100);
              };
          
              script.onerror = (error) => {
                console.error("Failed to load Google Maps script:", error);
                reject(new Error(`Failed to load Google Maps API: ${error}`));
              };
          
              console.log("Adding Google Maps script to head");
              document.head.appendChild(script);
            });
        },

        async initPlaces() {
            console.log("initPlaces called for state:", this.currentState);
            
            try {
                await this.loadGoogleMapsAPI();
                
                let field = null;
                if(this.currentState === AppState.Auth){
                    field = this.registerLocation;
                    console.log("Using register location field:", field);
                } else {
                    field = this.profileLocation;
                    console.log("Using profile location field:", field);
                }

                if (!field) {
                    console.error("No location field found for Places autocomplete");
                    return;
                }
                
                console.log("Creating Places Autocomplete...");
                
                // Add global error handler for Google Maps API errors
                if (!(window as any).gm_authFailure) {
                    (window as any).gm_authFailure = () => {
                        console.error("Google Maps API Key Error: The API key is invalid or the domain is not authorized");
                        this.showHUD("Google Maps authentication failed. Please check API key configuration.", true);
                    };
                }
                
                const autocomplete = new google.maps.places.Autocomplete(field, {
                    types: ['locality'],
                });
                
                autocomplete.addListener("place_changed", () => {
                    const place = autocomplete.getPlace();
                    console.log("Place selected:", place);
                    this.placeSelected = place;
                });

                console.log("Places Autocomplete initialized successfully");

            } catch (error) {
                console.error("Tripist: Error initializing Places Autocomplete:", error);
            }
        },

        register(): void {
            console.log("Register button clicked");
            console.log("Form values:", {
                firstName: this.registerFirstName.value,
                lastName: this.registerLastName.value,
                email: this.registerEmail.value,
                password: this.registerPassword.value ? "***" : "",
                confirmPassword: this.registerConfirmPassword.value ? "***" : "",
                location: this.registerLocation.value,
                placeSelected: this.placeSelected
            });

            // Ensure all fields are filled
            if (!this.registerFirstName.value || !this.registerLastName.value || !this.registerEmail.value || !this.registerPassword.value || !this.registerConfirmPassword.value) {
                console.log("Validation failed: Missing required fields");
                this.showHUD("Please fill in all fields.", true);
                return;
            }
            // Ensure passwords match
            if (this.registerPassword.value !== this.registerConfirmPassword.value) {
                console.log("Validation failed: Passwords don't match");
                this.showHUD("Passwords do not match.", true);
                return;
            }
            // Ensure user has selected a Places match
            if (!this.placeSelected) {
                console.log("Validation failed: No place selected");
                this.showHUD("Please type your location and select the best match.", true);
                return;
            }

            console.log("All validations passed, attempting to register...");
            this.showHUD("Creating account...");

            const credential = EmailAuthProvider.credential(this.registerEmail.value, this.registerPassword.value);

            const auth = getAuth();
            linkWithCredential(auth.currentUser, credential)
            .then((userCredential) => {
                console.log("Tripist: User registration successful:", userCredential);
                this.firebaseUser = userCredential.user;
                this.updateUserData({
                    first_name: this.registerFirstName.value,
                    last_name: this.registerLastName.value,
                    email: this.registerEmail.value,
                    location: this.registerLocation.value,
                    selectedCategories: this.selectedCategories,
                }).then(async () => {
                    console.log("User data updated successfully");
                    
                    // Update thread with user ID if there's an active thread
                    if (this.thread && this.lhpeUser) {
                        await this.updateThreadWithUserId();
                    }
                    
                    this.setState(AppState.Profile);
                }).catch((error: any) => {
                    console.error("Tripist: Error updating user data:", error);
                    this.showHUD("Error updating profile: " + error.message, true);
                });
            })
            .catch((error) => {
                const errorCode = error.code;
                const errorMessage = error.message;
                console.error("Tripist: Error registering:", errorCode, errorMessage);
                
                // Show user-friendly error messages
                let userMessage = "Registration failed. ";
                switch(errorCode) {
                    case 'auth/email-already-in-use':
                        userMessage += "This email is already registered.";
                        break;
                    case 'auth/invalid-email':
                        userMessage += "Please enter a valid email address.";
                        break;
                    case 'auth/weak-password':
                        userMessage += "Password should be at least 6 characters.";
                        break;
                    case 'auth/provider-already-linked':
                        userMessage += "This account is already registered.";
                        break;
                    default:
                        userMessage += errorMessage;
                }
                this.showHUD(userMessage, true);
            });
        },

        cancelRegistration(): void {
            // Navigate back to chat if thread exists, otherwise go to categories
            if (this.thread) {
                this.setState(AppState.Chat);
            } else {
                this.setState(AppState.Categories);
            }
        },

        updateProfile(): void {
            // Ensure all fields are filled
            if (!this.profileFirstName.value || !this.profileLastName.value || !this.profileEmail.value) {
                this.showHUD("Please fill in all fields.", true);
                return;
            }
            // Ensure user has selected a Places match
            if (!this.profileLocation.value) {
                this.showHUD("Please type your location and select the best match.", true);
                return;
            }
            // Ensure email is valid
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailPattern.test(this.profileEmail.value)) {
                this.showHUD("Please enter a valid email address.", true);
                return;
            }
            // Ensure an avatar is selected
            const selectedAvatar = this.profileAvatarContainer.querySelector(".tripist-avatar-selected");
            if (!selectedAvatar) {
                this.showHUD("Please select an avatar.", true);
                return;
            }
            this.updateUserData({
                first_name: this.profileFirstName.value,
                last_name: this.profileLastName.value,
                email: this.profileEmail.value,
                location: this.profileLocation.value,
                avatar_id: selectedAvatar.dataset.avatarId,
            }).then(() => {
                if(this.thread !== null){
                    this.setState(AppState.Chat);
                } else {
                    this.setState(AppState.Categories);
                }
            }).catch((error: any) => {
                console.error("Tripist: Error updating user data:", error);
            });

            
            
        },

        cancelProfile(): void {
            if (this.thread !== null) {
                this.setState(AppState.Chat);
            } else {
                this.setState(AppState.Categories);
            }
        },

        onSendClicked(): void {
            const message = this.textInput.value.trim();

            if(!message || (message === null || message === "") || this.streaming === true){
                return;
            }

            if (this.pendingInlineOptionMessageId) {
                return;
            }

            // Don't allow sending messages when in results states
            if (this.currentState === AppState.Results || this.currentState === AppState.ExploristResults) {
                return;
            }

            this.sendMessage(message);

            this.textInput.value = "";
        },

        setComposerLocked(lock: boolean): void {
            const forceHiddenByState =
                this.currentState === AppState.Results ||
                this.currentState === AppState.ExploristResults;
            const shouldHideComposer = forceHiddenByState;
            const shouldBlockComposer = lock && !forceHiddenByState;
            if (this.inputContainer) {
                this.inputContainer.classList.toggle("tripist-composer-hidden", shouldHideComposer);
                this.inputContainer.classList.toggle("tripist-composer-blocked", shouldBlockComposer);
                this.inputContainer.setAttribute("aria-hidden", shouldHideComposer ? "true" : "false");
                this.inputContainer.setAttribute("aria-disabled", shouldBlockComposer ? "true" : "false");
                this.inputContainer.style.display = shouldHideComposer ? "none" : "flex";
            }
            if (this.textInput) {
                this.textInput.disabled = shouldHideComposer || shouldBlockComposer;
            }
            if (this.sendButton) {
                this.sendButton.disabled = shouldHideComposer || shouldBlockComposer;
            }
        },

        syncInlineOptionComposerState(): void {
            if (!this.chatState?.messages || !Array.isArray(this.chatState.messages)) {
                this.pendingInlineOptionMessageId = null;
                this.setComposerLocked(false);
                return;
            }

            const pendingMessage = this.chatState.messages.find((message: any) => {
                if (!message?.id || !Array.isArray(message.tool_calls) || message.role !== "assistant") {
                    return false;
                }
                const selectedOption = this.getInlineOptionSelection(message.id);
                if (selectedOption) {
                    return false;
                }

                const hasOptions = message.tool_calls.some((toolCall: any) => this.extractInlineOptions(toolCall).length > 0);
                return hasOptions;
            });

            let pendingMessageId = pendingMessage ? pendingMessage.id : null;

            // DOM fallback: if message-state inference misses, keep composer blocked
            // whenever there is at least one enabled inline option button visible.
            if (!pendingMessageId && this.chatMessagesContainer) {
                const optionGroups = Array.from(
                    this.chatMessagesContainer.querySelectorAll(".tripist-message-options")
                );
                const hasPendingOptionGroup = optionGroups.some((group: Element) => {
                    const buttons = Array.from(group.querySelectorAll(".tripist-message-option"));
                    if (buttons.length === 0) {
                        return false;
                    }
                    return !buttons.some((button: Element) => button.classList.contains("selected"));
                });
                if (hasPendingOptionGroup) {
                    pendingMessageId = "pending-inline-options";
                }
            }

            this.pendingInlineOptionMessageId = pendingMessageId;
            this.setComposerLocked(!!this.pendingInlineOptionMessageId);
        },

        sendMessage(message: string | null = null, metadata: any = null): void {

            this.streaming = true;

            // Generate unique temporary IDs for this message exchange
            const tempOutboundId = `new-outbound-${Date.now()}`;
            const tempInboundId = `new-inbound-${Date.now()}`;
            this.currentTempOutboundId = tempOutboundId;
            this.currentTempInboundId = tempInboundId;
            this.currentMessageHasToolCalls = false;

            let xhr = new XMLHttpRequest();
            xhr.open("POST", `${this.apiBaseUrl}/tripist/${this.integrationId}/thread/${this.thread}`, true);
            xhr.setRequestHeader('Content-Type', 'application/json');
            const clientContextHeaders = this.getClientContextHeaders();
            (Object.keys(clientContextHeaders) as string[]).forEach((key) => {
                xhr.setRequestHeader(key, clientContextHeaders[key]);
            });

            let phase = message === null ? "intro" : "chat";

            let lastIndex = 0;
            let newMessage: any = {
                message,
                phase: phase,
                last_message_id: this.chatState.messages.length > 0 ? this.chatState.messages[0].id : null,
            }
            if (metadata && typeof metadata === "object") {
                newMessage.metadata = metadata;
            }

            switch (phase) {
                case "intro":
                    newMessage.categories = this.selectedCategories;
                    if(this.isIdentified && this.lhpeUser){
                        newMessage.userId = this.lhpeUser.id;
                    }
                    break;
            }

            if(message !== null){
                const userMessageMetadata: any = { phase: "chat" };
                if (metadata && typeof metadata === "object") {
                    Object.assign(userMessageMetadata, metadata);
                }
                this.chatState.messages.unshift({ 
                    id: tempOutboundId,
                    role: "user", 
                    content: [{ text: { value: message } }],
                    metadata: userMessageMetadata
                });
                this.chatIntroContainer.style.display = "none";
            }

            this.chatState.messages.unshift({ 
                id: tempInboundId,
                role: "assistant", 
                content: [{ text: { value: "..." } }],
                metadata: { phase: "chat" }
            });

            this.buildChatHistory();

            xhr.onprogress = (event) => {
                const currentResponse = xhr.responseText;
                const newChunk = currentResponse.substring(lastIndex);
                lastIndex = currentResponse.length;
                if (!newChunk) return;

                // Split the chunk on JSON/ boundaries -- a single progress event
                // can contain text + JSON or multiple segments.
                const jsonMarker = 'JSON/';
                const jsonStart = newChunk.indexOf(jsonMarker);

                if (jsonStart === -1) {
                    // Entire chunk is text
                    this.handleStreamText(newChunk);
                } else if (jsonStart === 0 && newChunk.endsWith('/')) {
                    // Entire chunk is a JSON envelope
                    try {
                        const json = JSON.parse(newChunk.substring(5, newChunk.length - 1));
                        this.handleInboundJSON(json);
                    } catch (e) {
                        console.error('Tripist: Failed to parse JSON envelope:', e);
                    }
                } else {
                    // Mixed: text before the JSON marker, then the JSON envelope
                    if (jsonStart > 0) {
                        this.handleStreamText(newChunk.substring(0, jsonStart));
                    }
                    const jsonPart = newChunk.substring(jsonStart);
                    if (jsonPart.startsWith(jsonMarker) && jsonPart.endsWith('/')) {
                        try {
                            const json = JSON.parse(jsonPart.substring(5, jsonPart.length - 1));
                            this.handleInboundJSON(json);
                        } catch (e) {
                            console.error('Tripist: Failed to parse JSON envelope:', e);
                        }
                    }
                }
            }

            xhr.onloadend = (event) => {
                this.streaming = false;
                // Final markdown pass after stream completion so partially streamed
                // structures (for example numbered lists) render correctly.
                this.updateInboundMessage().catch((error: any) => {
                    console.error("Tripist: Failed to finalize inbound message render:", error);
                });
            }

            xhr.send(JSON.stringify(newMessage));

        },



        async buildChatHistory(): Promise<void> {

            // Prevent concurrent execution
            if (this.buildingChatHistory) {
                return;
            }
            this.buildingChatHistory = true;

            try {
                //console.log("Building chat history", this.chatState.messages);

                // Rebuild all messages
                for (let i = this.chatState.messages.length - 1; i >= 0; i--) {
                    const message = this.chatState.messages[i];
                    
                    // If the message is an intro message, do nothing
                    if(message.metadata && message.metadata.phase === "intro") {
                    } 
                    // Skip rendering empty message shells.
                    else if (!this.shouldRenderMessage(message)) {
                        const existingElem = this.chatMessagesContainer.querySelector('#'+message.id);
                        if (existingElem) {
                            existingElem.remove();
                        }
                    }
                    // If there isn't an element with the message id, create it
                    else if (this.chatMessagesContainer.querySelector('#'+message.id) === null) {
                        const elem = await this.makeMessageElement(message);
                        // Double-check the element doesn't exist after async operation
                        if (this.chatMessagesContainer.querySelector('#'+message.id) === null) {
                            this.chatMessagesContainer.appendChild(elem);
                        }
                    }
                };

                setTimeout(() => {
                    this.updateMessageTools();
                }, 100);

            } finally {
                this.buildingChatHistory = false;
            }

        },

        shouldRenderMessage(message: any): boolean {
            if (!message) {
                return false;
            }
            const textValue = typeof message?.content?.[0]?.text?.value === "string"
                ? message.content[0].text.value
                : "";
            const trimmedText = textValue.trim();
            const hasVisibleText = trimmedText.length > 0;
            const isStreamingPlaceholder = trimmedText === "...";
            const hasInlineOptions = Array.isArray(message?.tool_calls) && message.tool_calls.some(
                (toolCall: any) => this.extractInlineOptions(toolCall).length > 0
            );

            if (message.role === "assistant") {
                return hasVisibleText || isStreamingPlaceholder || hasInlineOptions;
            }
            return hasVisibleText || isStreamingPlaceholder;
        },

        handleStreamText(raw: string): void {
            const processedChunk = raw
                .split(/\r?\n/)
                .filter(line => !line.trim().startsWith(':'))
                .join('\n');
            const hasVisibleText = processedChunk.trim().length > 0;

            // Preserve newline-only chunks so streamed Markdown keeps structure
            // (for example, blank lines that terminate list items).
            if (processedChunk.length === 0) return;

            if (this.currentMessageHasToolCalls) {
                // Don't create the follow-up message until we have visible text.
                if (!hasVisibleText) return;

                const newTempInboundId = `new-inbound-followup-${Date.now()}`;
                this.currentTempInboundId = newTempInboundId;
                this.currentMessageHasToolCalls = false;

                this.chatState.messages.unshift({
                    id: newTempInboundId,
                    role: "assistant",
                    content: [{ text: { value: processedChunk } }],
                    metadata: { phase: "chat" }
                });

                this.buildChatHistory();
                this.updateInboundMessage();
            } else {
                let streamingMessage = this.chatState.messages.find((msg: any) => msg.id === this.currentTempInboundId);
                if (!streamingMessage) {
                    streamingMessage = this.chatState.messages.find((msg: any) => msg.role === "assistant");
                }

                if (streamingMessage?.content?.[0]?.text) {
                    if (streamingMessage.content[0].text.value === "...") {
                        // Keep ellipsis loader visible until non-whitespace content arrives.
                        if (!hasVisibleText) return;
                        streamingMessage.content[0].text.value = "";
                    }
                    streamingMessage.content[0].text.value += processedChunk;
                    this.updateInboundMessage();
                }
            }
        },

        handleInboundJSON(json: any): void {
            console.log('Inbound JSON: ', JSON.stringify(json, null, 2));

            // Update message ids - find messages by their current IDs instead of assuming positions
            let outboundElem = this.chatMessagesContainer.querySelector(`#${this.currentTempOutboundId}`) as HTMLElement;
            if(json.new_outbound_id !== undefined) {
                if (outboundElem !== null) {
                    outboundElem.setAttribute("id", json.new_outbound_id);
                }
                // Find the message with the current temp ID instead of hardcoded one
                const outboundMessage = this.chatState.messages.find((msg: any) => msg.id === this.currentTempOutboundId);
                if (outboundMessage) {
                    outboundMessage.id = json.new_outbound_id;
                }
                // Keep stream pointers aligned with server-assigned IDs.
                this.currentTempOutboundId = json.new_outbound_id;
            }
            let inboundElem = this.chatMessagesContainer.querySelector(`#${this.currentTempInboundId}`) as HTMLElement;
            if(json.new_inbound_id !== undefined) {
                if (inboundElem !== null) {
                    inboundElem.setAttribute("id", json.new_inbound_id);
                }
                // Find the message with the current temp ID instead of hardcoded one
                const inboundMessage = this.chatState.messages.find((msg: any) => msg.id === this.currentTempInboundId);
                if (inboundMessage) {
                    inboundMessage.id = json.new_inbound_id;
                }
                // Keep stream pointers aligned with server-assigned IDs.
                this.currentTempInboundId = json.new_inbound_id;
            }

            // Tool calls - find the inbound message instead of assuming it's at position 0
            if(json.tool_calls !== undefined){
                console.log('Tool Calls: ', JSON.stringify(json.tool_calls, null, 2));
                const assistantType = this.config.integration?.type;
                if (assistantType === "aura_assistant" || assistantType === "tripist_assistant" || assistantType === "explorist_assistant") {
                    const toolNames = json.tool_calls
                        .map((toolCall: any) => toolCall?.function?.name)
                        .filter((name: any) => !!name);
                    const presentationTools = toolNames.filter((name: string) =>
                        name === "present_choice_options" ||
                        name === "present_place_options" ||
                        name === "present_booking_options" ||
                        name === "present_timeslot_options"
                    );
                    console.log("Choice tool diagnostics:", {
                        toolNames,
                        presentationToolsCount: presentationTools.length
                    });
                    if (presentationTools.length === 0 && assistantType === "aura_assistant") {
                        console.warn("Aura response has no choice presentation tool call in this payload.");
                    }
                }
                
                // Find the current inbound message (assistant message) to add tool calls to
                const inboundMessage = this.chatState.messages.find((msg: any) => 
                    msg.id === this.currentTempInboundId || msg.id === json.new_inbound_id
                );
                if (inboundMessage) {
                    inboundMessage.tool_calls = json.tool_calls;
                    this.currentMessageHasToolCalls = true;
                }

                json.tool_calls.forEach((toolCall: any) => {
                    console.log('Tool Call: ', toolCall.function.name);
                });
                this.updateMessageTools();
            }

            // With streaming tool outputs, we no longer need to poll for more messages
            // The stream should continue automatically

            // An error occurred
            if(json.error !== undefined) {
                console.error("Tripist: Error: ", json.error);
                this.showHUD("An error occurred: " + json.error, true);
                return;
            }

        },

        async updateInboundMessage() {
            // Find the current inbound message being streamed
            // First try to find the message with the current temp inbound ID
            let message = this.chatState.messages.find((msg: any) => msg.id === this.currentTempInboundId);
            
            // If not found, find the most recent assistant message (should be first in array)
            if (!message) {
                message = this.chatState.messages.find((msg: any) => msg.role === "assistant");
            }
            
            // Final fallback to the first message
            if (!message) {
                message = this.chatState.messages[0];
            }

            if (!message || !message.content || !message.content[0] || !message.content[0].text) {
                return; // Safety check
            }

            if (!this.shouldRenderMessage(message)) {
                const messageElem = this.chatMessagesContainer.querySelector('#'+message.id) as HTMLElement;
                if (messageElem) {
                    messageElem.remove();
                }
                return;
            }

            let html = await marked.parse(message.content[0].text.value);

            let messageElem = this.chatMessagesContainer.querySelector('#'+message.id) as HTMLElement;

            if(messageElem !== null) {
                const textElement = messageElem.querySelector(".tripist-message-text");
                if (textElement) {
                    const existingOptionsContainer = textElement.querySelector(".tripist-message-options") as HTMLElement | null;
                    const isEllipsisPlaceholder = message.content[0].text.value.trim() === "...";
                    if (isEllipsisPlaceholder) {
                        textElement.classList.add("tripist-message-text-loading");
                        textElement.innerHTML = "";
                        textElement.appendChild(this.createEllipsisLoader());
                    } else {
                        textElement.classList.remove("tripist-message-text-loading");
                        textElement.innerHTML = html;
                        if (existingOptionsContainer) {
                            textElement.appendChild(existingOptionsContainer);
                        }
                    }
                }
            }

            this.scrollToEndOfChat();
        },

        async updateMessageTools() {

            // loop through the messages and create tools as needed
            for (let i = 0; i < this.chatState.messages.length; i++) {
                const message = this.chatState.messages[i];

                if(message.tool_calls !== undefined) {

                    console.log("Tools called: ", message.tool_calls);

                    message.tool_calls.forEach((toolCall: any) => {

                        const assistantType = this.config.integration?.type;
                        console.log("Processing tool call:", toolCall.function.name, "Assistant type:", assistantType);

                        // Present Results

                        // Tripist Assistants results are a PDF download
                        if (assistantType === "tripist_assistant" && toolCall.function.name.trim() === "final_results") {

                            console.log("Present Results - Tripist Assistant");
                            this.prepareResultsTools();

                        }

                        // Explorist Assistants results are displayed in the chat area
                        else if (
                            assistantType === "explorist_assistant" &&
                            (toolCall.function.name.trim() === "final_results" || toolCall.function.name.trim() === "get_category_results")
                        ) {

                            console.log("Present Explorist Results - Explorist Assistant");
                            this.prepareExploristResultsTools(toolCall);

                        }

                        // Handle final_results for any other assistant type (fallback to tripist behavior)
                        else if (toolCall.function.name.trim() === "final_results") {

                            console.log("Present Results (fallback) - Unknown type:", assistantType);
                            this.prepareResultsTools();

                        }

                        // Present Restart
                        else if (toolCall.function.name.trim() == "restart") {

                            console.log("Present Restart");

                            this.prepareResetTools();

                        }

                        // Unhandled Tool
                        else {
                            console.log("Unhandled tool: ", toolCall.function.name);
                        }

                    });
                    this.renderInlineOptionsForMessage(message);
                }
                
            }

            this.syncInlineOptionComposerState();

        },

        prepareResultsTools(): void {

            if(!this.disableResultsCache && this.getLocalStorage("results")) {
                console.log("Cached results found", this.getLocalStorage("results"));
                this.downloadUrl = this.getLocalStorage("results");
                this.shareDownloadUrl = this.getLocalStorage("results_share");
            } else if (this.disableResultsCache) {
                this.downloadUrl = null;
                this.shareDownloadUrl = null;
            }

            let mssgTools: any[] = [];

            // Download
            let downloadAction = this.makeActionElement("tripist-tool-download", "Download recos", "Download", async () => {
                this.prepareDownload("download").then((url: string | null | undefined) => {
                    if (!url) {
                        return;
                    }
                    const link = document.createElement('a');
                    link.target = "_blank";
                    link.href = url;
                    link.click();
                });
            });
            mssgTools.push(downloadAction);

            // Share
            let shareAction = this.makeActionElement("tripist-tool-share", "Share recos", "Share", async () => {
                this.prepareDownload("share").then(async (url: string | null | undefined) => {
                    if (!url) {
                        return;
                    }
                    // Record the share in our analytics
                    await this.recordThreadShare();
                    
                    // Proceed with native sharing
                    navigator.share({
                        title: "My Tripist Recommendations",
                        text: "Check out these recommendations from Tripist",
                        url: url,
                    }).then(() => {
                        console.log("Share successful");
                    }).catch((error) => {
                        console.error("Error sharing:", error);
                    });
                });

            });
            mssgTools.push(shareAction);

            // Like
            let likeAction = this.makeActionElement("tripist-tool-like", "Submit Feedback", "Feedback", async () => {
                this.showFeedbackModal();
            });
            mssgTools.push(likeAction);

            // Reset
            let resetAction = this.makeActionElement("tripist-tool-reset", "Start New Chat", "New Chat", async () => {
                this.reset();
            });
            mssgTools.push(resetAction);

            this.renderTools(mssgTools);

            this.setState(AppState.Results);
        
        },

        async prepareDownload(context: "download" | "share" = "download") {

            const cacheKey = context === "share" ? "results_share" : "results";
            const currentUrl = context === "share" ? this.shareDownloadUrl : this.downloadUrl;

            if(!this.disableResultsCache && currentUrl !== null){
                console.log("Using cached results", currentUrl);
                return currentUrl;
            }

            if (!this.thread) {
                console.error("Download aborted: missing thread id");
                this.hideHUD();
                return;
            }

            this.showHUD("Preparing download...");

            const resolvedUserId = this.lhpeUser?.id || this.lhpeUser?.user_id || this.lhpeUser?.userId || '';

            // Build base URL with required parameters
            let downloadUrl = `${this.apiBaseUrl}/tripist/${this.integrationId}/thread/${this.thread}/download?userId=${resolvedUserId}&avatarUrl=${encodeURIComponent(this.getAvatarUrl())}&pdf_context=${context}`;

            let response = await fetch(downloadUrl, {
                method: "GET",
                headers: { "Content-Type": "application/json" }
            });

            if (!response.ok) {
                console.error("Download failed with status:", response.status);
                this.hideHUD();
                return;
            }

            // Convert response to JSON
            const data = await response.json();

            if (context === "share") {
                this.shareDownloadUrl = data.url;
            } else {
                this.downloadUrl = data.url;
            }

            if (!this.disableResultsCache) {
                this.setLocalStorage(cacheKey, data.url);
            }

            this.hideHUD();

            return data.url;

            /*
            try {

                const link = document.createElement('a');
                link.target = "_blank";
                link.href = this.downloadUrl;
                link.click();

            } catch (err) {
                console.error("Error downloading file:", err);
            }
            */
        },

        prepareResetTools(): void {

            console.log("Present Restart Start");

            // Reset
            let resetAction = this.makeActionElement("tripist-tool-reset", "Start New Chat", "New Chat", async () => {
                this.reset();
            });

            this.renderTools([resetAction]);

            this.setState(AppState.Results);

        },

        prepareExploristResultsTools(toolCall: any): void {
            console.log("Preparing Explorist Results Toolbar", toolCall);

            // Store the results data for later use
            this.exploristResultsData = toolCall;

            let mssgTools: any[] = [];

            // Display Results
            let displayResultsAction = this.makeActionElement("tripist-tool-display-results", "Display Results", "Display Results", async () => {
                this.displayExploristResults();
            });
            mssgTools.push(displayResultsAction);

            this.renderTools(mssgTools);
            this.setState(AppState.Results);
        },

        displayExploristResults(): void {
            if (this.exploristResultsData) {
                this.prepareExploristResults(this.exploristResultsData);
                this.setState(AppState.ExploristResults);
                this.exploristResultsData = null; // Clear the stored data
            }
        },

        prepareExploristResults(toolCall: any): void {
            console.log("Preparing Explorist Results", toolCall);

            // Extract the JSON results from the tool call arguments
            let resultsData = null;
            try {
                if (toolCall.function.arguments) {
                    resultsData = JSON.parse(toolCall.function.arguments);
                }
            } catch (error) {
                console.error("Error parsing tool call arguments:", error);
                return;
            }

            if (!resultsData || !resultsData.results) {
                console.error("No results data found in tool call");
                return;
            }

            // Populate the header from config
            console.log("Results data:", resultsData);
            this.exploristResultsTitle.textContent = this.config.integration.config.results_title || "WE FOUND EXACTLY WHAT YOU NEED!";
            this.exploristResultsMessage.textContent = this.config.integration.config.results_subtitle || "Based on our conversation, we can confidently recommend the following to you:";

            // Clear existing results
            this.exploristResultsList.innerHTML = "";

            // Create result items
            resultsData.results.forEach((item: any) => {
                const resultItem = document.createElement("div");
                resultItem.className = "tripist-explorist-result-item";

                // Image container
                const imageContainer = document.createElement("div");
                imageContainer.className = "tripist-explorist-result-image-container";

                // Image
                if (item.image) {
                    const image = document.createElement("img");
                    image.className = "tripist-explorist-result-image";
                    image.src = item.image;
                    image.alt = item.name || "Product image";
                    imageContainer.appendChild(image);
                }

                // Subtitle (price) under image
                if (item.subtitle) {
                    const subtitleDiv = document.createElement("div");
                    subtitleDiv.className = "tripist-explorist-result-subtitle";
                    subtitleDiv.textContent = item.subtitle;
                    imageContainer.appendChild(subtitleDiv);
                }

                resultItem.appendChild(imageContainer);

                // Content container
                const contentDiv = document.createElement("div");
                contentDiv.className = "tripist-explorist-result-content";

                // Title (name)
                if (item.name) {
                    const titleDiv = document.createElement("div");
                    titleDiv.className = "tripist-explorist-result-title";
                    titleDiv.textContent = item.name;
                    contentDiv.appendChild(titleDiv);
                }

                // Description
                if (item.description) {
                    const descriptionDiv = document.createElement("div");
                    descriptionDiv.className = "tripist-explorist-result-description";
                    descriptionDiv.textContent = item.description;
                    contentDiv.appendChild(descriptionDiv);
                }

                // Buy Now button
                if (item.url) {
                    const button = document.createElement("a");
                    button.className = "tripist-explorist-result-button";
                    button.href = item.url;
                    button.target = "_blank";
                    button.rel = "noopener noreferrer";
                    button.textContent = "Buy Now";
                    contentDiv.appendChild(button);
                }

                resultItem.appendChild(contentDiv);
                this.exploristResultsList.appendChild(resultItem);
            });
        },

        renderTools(mssgTools: [any]): void {
            console.log("Tools: ", mssgTools.length);
            
            this.actionsContainer.innerHTML = "";

            if(mssgTools.length > 0) {

                mssgTools.forEach((tool: any) => {
                    this.actionsContainer.appendChild(tool);
                });

            }

            this.scrollToEndOfChat();
        },

        makeActionElement(className: string, label: string, mobileLabel: string, action: any): HTMLElement {
            let actionElem = document.createElement("div");
            actionElem.className = "tripist-tool " + className;
            
            let iconElem = document.createElement("div");
            iconElem.className = "tripist-tool-icon";
            actionElem.appendChild(iconElem);
            
            let labelElem = document.createElement("div");
            labelElem.className = "tripist-tool-label";
            labelElem.textContent = label;
            actionElem.appendChild(labelElem);

            let mobileLabelElem = document.createElement("div");
            mobileLabelElem.className = "tripist-tool-mobile-label";
            mobileLabelElem.textContent = mobileLabel;
            actionElem.appendChild(mobileLabelElem);

            actionElem.addEventListener("click", action);

            return actionElem;
        },

        getInlineOptionState(): Record<string, string> {
            try {
                const raw = this.getLocalStorage("inline_option_state");
                return raw ? JSON.parse(raw) : {};
            } catch (err) {
                return {};
            }
        },

        setInlineOptionSelection(messageId: string, value: string): void {
            const state = this.getInlineOptionState();
            state[messageId] = value;
            this.setLocalStorage("inline_option_state", JSON.stringify(state));
        },

        getInlineOptionSelection(messageId: string): string | null {
            const state = this.getInlineOptionState();
            return state[messageId] || null;
        },

        normalizeInlineSelectionText(value: string | null | undefined): string {
            if (!value) return "";
            return value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
        },

        inferInlineOptionSelectionFromHistory(messageId: string, options: any[]): string | null {
            if (!Array.isArray(this.chatState?.messages) || options.length === 0) {
                return null;
            }

            const messageIdx = this.chatState.messages.findIndex((msg: any) => msg?.id === messageId);
            if (messageIdx <= 0) {
                return null;
            }

            // First preference: explicit selection metadata sent with the user message.
            for (let i = messageIdx - 1; i >= 0; i--) {
                const msg = this.chatState.messages[i];
                if (msg?.role === "user" && msg?.metadata?.selection) {
                    const selection = msg.metadata.selection;
                    if (selection.source_message_id === messageId && selection.option_id) {
                        return String(selection.option_id);
                    }
                }
            }

            // Fallback: infer from nearest newer user reply text.
            let userReplyText = "";
            for (let i = messageIdx - 1; i >= 0; i--) {
                const msg = this.chatState.messages[i];
                if (msg?.role === "user" && msg?.content?.[0]?.text?.value) {
                    userReplyText = msg.content[0].text.value;
                    break;
                }
                // Stop at the next assistant message that starts another options prompt.
                if (
                    msg?.role === "assistant" &&
                    Array.isArray(msg?.tool_calls) &&
                    msg.tool_calls.some((toolCall: any) => this.extractInlineOptions(toolCall).length > 0)
                ) {
                    break;
                }
            }

            const normalizedUserReply = this.normalizeInlineSelectionText(userReplyText);
            if (!normalizedUserReply) {
                return null;
            }

            for (const option of options) {
                const optionId = String(option?.id || "");
                const optionLabel = this.normalizeInlineSelectionText(option?.label);
                const optionMessage = this.normalizeInlineSelectionText(option?.message);

                if (
                    (optionMessage && (normalizedUserReply.includes(optionMessage) || optionMessage.includes(normalizedUserReply))) ||
                    (optionLabel && (normalizedUserReply.includes(optionLabel) || optionLabel.includes(normalizedUserReply)))
                ) {
                    return optionId || null;
                }
            }

            return null;
        },

        reconcileOptionToolCallsFromSelectionMetadata(): void {
            if (!Array.isArray(this.chatState?.messages)) {
                return;
            }

            const userMessagesWithSelection = this.chatState.messages.filter((msg: any) =>
                msg?.role === "user" &&
                msg?.metadata?.selection?.source_message_id &&
                msg?.metadata?.selection?.option_id
            );

            userMessagesWithSelection.forEach((userMsg: any) => {
                const selection = userMsg.metadata.selection;
                const sourceMessageId = selection.source_message_id;
                const selectedOptionId = String(selection.option_id);
                const sourceToolCallId = selection.source_tool_call_id ? String(selection.source_tool_call_id) : null;
                const sourceAssistantMessage = this.chatState.messages.find((msg: any) =>
                    msg?.id === sourceMessageId && msg?.role === "assistant"
                );

                if (!sourceAssistantMessage) {
                    return;
                }

                const sourceHasMatchingOption = Array.isArray(sourceAssistantMessage.tool_calls) &&
                    sourceAssistantMessage.tool_calls.some((toolCall: any) =>
                        (sourceToolCallId && String(toolCall?.id || "") === sourceToolCallId) ||
                        this.extractInlineOptions(toolCall).some((opt: any) => String(opt.id) === selectedOptionId)
                    );

                if (sourceHasMatchingOption) {
                    this.setInlineOptionSelection(sourceMessageId, selectedOptionId);
                    return;
                }

                const donorAssistantMessage = this.chatState.messages.find((msg: any) => {
                    if (!msg || msg.role !== "assistant" || msg.id === sourceMessageId || !Array.isArray(msg.tool_calls)) {
                        return false;
                    }
                    return msg.tool_calls.some((toolCall: any) => {
                        if (sourceToolCallId && String(toolCall?.id || "") === sourceToolCallId) {
                            return true;
                        }
                        return this.extractInlineOptions(toolCall).some((opt: any) => String(opt.id) === selectedOptionId);
                    });
                });

                if (!donorAssistantMessage || !Array.isArray(donorAssistantMessage.tool_calls)) {
                    return;
                }

                const movedToolCalls = donorAssistantMessage.tool_calls.filter((toolCall: any) =>
                    (sourceToolCallId && String(toolCall?.id || "") === sourceToolCallId) ||
                    this.extractInlineOptions(toolCall).some((opt: any) => String(opt.id) === selectedOptionId)
                );

                if (movedToolCalls.length === 0) {
                    return;
                }

                donorAssistantMessage.tool_calls = donorAssistantMessage.tool_calls.filter((toolCall: any) =>
                    !(
                        (sourceToolCallId && String(toolCall?.id || "") === sourceToolCallId) ||
                        this.extractInlineOptions(toolCall).some((opt: any) => String(opt.id) === selectedOptionId)
                    )
                );

                if (!Array.isArray(sourceAssistantMessage.tool_calls)) {
                    sourceAssistantMessage.tool_calls = [];
                }
                sourceAssistantMessage.tool_calls.push(...movedToolCalls);
                this.setInlineOptionSelection(sourceMessageId, selectedOptionId);
            });
        },

        extractInlineOptions(toolCall: any): any[] {
            if (!toolCall?.function?.arguments) {
                return [];
            }

            try {
                const toolName = toolCall?.function?.name;
                const assistantType = this.config.integration?.type;
                const isAuraAssistant = assistantType === "aura_assistant";
                const args = JSON.parse(toolCall.function.arguments);
                const isExplicitPresentationTool =
                    toolName === "present_choice_options" ||
                    toolName === "present_place_options" ||
                    toolName === "present_booking_options" ||
                    toolName === "present_timeslot_options";

                if (isExplicitPresentationTool && Array.isArray(args?.options)) {
                    const declaredKind = typeof args?.choice_kind === "string" ? args.choice_kind : null;
                    const inferredKind =
                        toolName === "present_timeslot_options"
                            ? "timeslot"
                            : toolName === "present_booking_options"
                                ? "booking"
                                : toolName === "present_place_options"
                                    ? "place"
                                    : "generic";

                    return args.options.map((option: any) => ({
                        id: String(option.id || option.value || option.label || option.name),
                        label: option.label || option.name || "Option",
                        message:
                            option.submit_message ||
                            option.message ||
                            `I choose ${option.label || option.name || "this option"}.`,
                        kind: option.kind || declaredKind || inferredKind,
                        style: option.style === "escape" ? "escape" : "default",
                        source_tool_call_id: toolCall?.id || null,
                        source_tool_name: toolName || null
                    }));
                }

                // final_results payloads can include a results array, but those are display data,
                // not forced-choice options, and should never render as inline buttons.
                if (toolName === "final_results") {
                    return [];
                }

                // Backward-compatible fallback during migration.
                if (isAuraAssistant && Array.isArray(args?.options)) {
                    return args.options.map((option: any) => ({
                        id: String(option.document_id || option.id || option.name),
                        label: option.name || option.label || "Option",
                        message: `I want to book ${option.name || option.label}.`,
                        kind: "place",
                        style: "default",
                        source_tool_call_id: toolCall?.id || null,
                        source_tool_name: toolName || null
                    }));
                }

                if (isAuraAssistant && Array.isArray(args?.results)) {
                    return args.results.map((result: any) => ({
                        id: String(result.document_id || result.id || result.name),
                        label: result.name || "Place",
                        message: result.document_id
                            ? `I want to book ${result.name} (document ${result.document_id}).`
                            : `I want to book ${result.name}.`,
                        kind: "place",
                        style: "default",
                        source_tool_call_id: toolCall?.id || null,
                        source_tool_name: toolName || null
                    }));
                }

                if (isAuraAssistant && Array.isArray(args?.availability?.slots)) {
                    return args.availability.slots
                        .filter((slot: any) => slot.available)
                        .map((slot: any) => ({
                            id: String(slot.start_at),
                            label: new Date(slot.start_at).toLocaleString(),
                            message: `That time works for me: ${new Date(slot.start_at).toLocaleString()}.`,
                            kind: "timeslot",
                            style: "default",
                            source_tool_call_id: toolCall?.id || null,
                            source_tool_name: toolName || null
                        }));
                }
            } catch (err) {
                console.error("Unable to parse inline options:", err);
            }

            return [];
        },

        extractInlineOptionsPrompt(toolCall: any): string | null {
            if (!toolCall?.function?.arguments) {
                return null;
            }
            try {
                const args = JSON.parse(toolCall.function.arguments);
                if (typeof args?.prompt !== "string") {
                    return null;
                }
                const trimmedPrompt = args.prompt.trim();
                return trimmedPrompt.length > 0 ? trimmedPrompt : null;
            } catch (err) {
                return null;
            }
        },

        renderInlineOptionsForMessage(message: any): void {
            if (!message?.id || !Array.isArray(message.tool_calls)) {
                return;
            }

            const messageElem = this.chatMessagesContainer.querySelector("#" + message.id) as HTMLElement | null;
            if (!messageElem) {
                return;
            }
            const contentContainer = messageElem.querySelector(".tripist-message-content") as HTMLElement | null;
            if (!contentContainer) {
                return;
            }
            const messageTextContainer = contentContainer.querySelector(".tripist-message-text") as HTMLElement | null;
            if (!messageTextContainer) {
                return;
            }

            // Remove any prior inline options for this message (legacy or current placement)
            const oldContainer = messageElem.querySelector(".tripist-message-options");
            if (oldContainer) {
                oldContainer.remove();
            }

            let options: any[] = [];
            let sourceToolName: string | null = null;
            let sourceToolPrompt: string | null = null;
            for (let i = message.tool_calls.length - 1; i >= 0; i--) {
                options = this.extractInlineOptions(message.tool_calls[i]);
                if (options.length > 0) {
                    sourceToolName = message.tool_calls[i]?.function?.name || null;
                    sourceToolPrompt = this.extractInlineOptionsPrompt(message.tool_calls[i]);
                    break;
                }
            }
            if (options.length === 0) {
                return;
            }

            const currentMessageTextValue = typeof message?.content?.[0]?.text?.value === "string"
                ? message.content[0].text.value
                : "";
            const normalizedCurrentMessageText = currentMessageTextValue.trim().toLowerCase().replace(/\s+/g, " ");
            const genericOptionPromptTexts = new Set([
                "please choose an option below.",
                "please choose using the buttons below.",
                "choose from the buttons below.",
                "select one option below."
            ]);
            const shouldReplaceMessageTextWithToolPrompt = !!sourceToolPrompt && (
                normalizedCurrentMessageText.length === 0 ||
                normalizedCurrentMessageText === "..." ||
                genericOptionPromptTexts.has(normalizedCurrentMessageText)
            );

            if (shouldReplaceMessageTextWithToolPrompt) {
                if (!Array.isArray(message.content) || message.content.length === 0) {
                    message.content = [{ text: { value: sourceToolPrompt } }];
                } else if (!message.content[0]?.text || typeof message.content[0].text !== "object") {
                    message.content[0].text = { value: sourceToolPrompt };
                } else {
                    message.content[0].text.value = sourceToolPrompt;
                }
                messageTextContainer.textContent = sourceToolPrompt;
            }

            let selectedOption = this.getInlineOptionSelection(message.id);
            if (!selectedOption) {
                const inferredSelection = this.inferInlineOptionSelectionFromHistory(message.id, options);
                if (inferredSelection) {
                    selectedOption = inferredSelection;
                    this.setInlineOptionSelection(message.id, inferredSelection);
                }
            }
            const optionsContainer = document.createElement("div");
            optionsContainer.className = "tripist-message-options";

            options.forEach((option: any) => {
                const button = document.createElement("button");
                button.className = "tripist-message-option";
                if (option.kind === "timeslot") {
                    button.classList.add("tripist-message-option-timeslot");
                } else {
                    button.classList.add("tripist-message-option-place");
                }
                if (option.style === "escape") {
                    button.classList.add("tripist-message-option-escape");
                }
                button.textContent = option.label;
                button.setAttribute("aria-label", option.label);
                const isSelected = selectedOption === option.id;
                const hasSelection = !!selectedOption;
                if (isSelected) {
                    button.classList.add("selected");
                }
                // Once one option is chosen for this message, lock all options.
                button.disabled = hasSelection;
                button.setAttribute("aria-disabled", hasSelection ? "true" : "false");

                button.addEventListener("click", () => {
                    this.setInlineOptionSelection(message.id, option.id);
                    this.sendMessage(option.message, {
                        selection: {
                            source_message_id: message.id,
                            source_tool_call_id: option.source_tool_call_id || null,
                            source_tool_name: option.source_tool_name || null,
                            option_id: option.id,
                            option_label: option.label,
                            option_kind: option.kind || null
                        }
                    });
                    this.renderInlineOptionsForMessage(message);
                    this.syncInlineOptionComposerState();
                });
                optionsContainer.appendChild(button);
            });

            // Keep options visually bound to the assistant bubble that generated them.
            messageTextContainer.appendChild(optionsContainer);
        },

        createEllipsisLoader(): HTMLElement {
            const loader = document.createElement("span");
            loader.className = "tripist-ellipsis-loader";

            for (let i = 0; i < 3; i++) {
                const dot = document.createElement("span");
                dot.className = "tripist-ellipsis-loader-dot";
                loader.appendChild(dot);
            }

            return loader;
        },

        async makeMessageElement(message: any): Promise<HTMLElement> {

            //console.log("Making message element: ", message);

            let html = "";

            if(message.content.length > 0) {
                html = await marked.parse(message.content[0].text.value);
            }

            const messageContainer = document.createElement("div");
            messageContainer.id = message.id;
            messageContainer.classList.add("tripist-message");

            const contentContainer = document.createElement("div");
            contentContainer.classList.add("tripist-message-content");

            let messageIcon = document.createElement("div");
            messageIcon.classList.add("tripist-message-icon");
            let iconImage = document.createElement("img");
            iconImage.classList.add("tripist-message-icon-img");
            messageIcon.appendChild(iconImage);
            contentContainer.appendChild(messageIcon);
            let messageText = document.createElement("div");
            messageText.classList.add("tripist-message-text");

            const isEllipsisPlaceholder = message.content.length > 0 && message.content[0].text.value.trim() === "...";

            if (isEllipsisPlaceholder) {
                messageText.classList.add("tripist-message-text-loading");
                messageText.appendChild(this.createEllipsisLoader());
            } else if (html) {
                messageText.innerHTML = html;
            } else {
                messageText.innerHTML = "";
            }
            contentContainer.appendChild(messageText);

            messageContainer.appendChild(contentContainer);

            if (message.role === "assistant") {
                messageContainer.classList.add("tripist-message-inbound");
                messageIcon.classList.add("tripist-assistant-icon");
                iconImage.classList.add("tripist-assistant-icon-img");
                iconImage.src = this.baseUrl + "/assets/default/icon.svg";
            } else {
                messageContainer.classList.add("tripist-message-outbound");
                messageIcon.classList.add("tripist-user-icon");
                iconImage.classList.add("tripist-user-icon-img");
                iconImage.src = this.getAvatarUrl();
            }

            return messageContainer;
        },

        async toolClicked(messageId: string, tool: string, action: string): Promise<void> {

            //console.log("Tool clicked: ", messageId, tool, action);

            switch(tool) {
                case "final_results":
                    switch(action) {
                        case "download":
                            try {

                                this.showHUD("Preparing results...");

                                const response = await fetch(`${this.apiBaseUrl}/tripist/${this.integrationId}/thread/${this.thread}/results/${messageId}/download`, {
                                  method: "GET",
                                  headers: { "Content-Type": "application/json" }
                                });

                                this.hideHUD();

                                if (!response.ok) {
                                  console.error("Download failed with status:", response.status);
                                  return;
                                }
                                
                                // Convert response to JSON
                                const data = await response.json();
                                window.open(data.url, '_blank');

                              } catch (err) {
                                console.error("Error downloading file:", err);
                              }
                              break;
                        case "share":
                            console.log("Share results");
                            // Record the share in our analytics
                            await this.recordThreadShare();
                            break;
                        case "like":
                            this.showFeedbackModal();
                            break;
                        default:
                            console.log("Unhandled action: ", action);
                            break;
                    }
                    break;
                default:
                    console.log("Unhandled tool: ", tool);
                    break;
            }

        },

        showHUD(message: string = "", showButton: boolean = false): void {
            // If no message is provided, use default based on whether it's an error
            if (!message) {
                message = showButton ? "A Fatal Error Has Occurred" : "Please Wait";
            }
            this.hudMessage.textContent = message;
            if (showButton) {
                this.hudButton.style.display = "block";
            } else {
                this.hudButton.style.display = "none";
            }
            this.hud.style.display = "flex";
        },

        hideHUD(): void {
            this.hud.style.display = "none";
        },

        scrollToEndOfChat(): void {
            setTimeout(() => {
                var scrollableDiv = this.body;
                scrollableDiv!.scrollTop = scrollableDiv!.scrollHeight;
            }, 100);
        },

        getBrowserModel(browserInfo: BrowserInfo): string {
            if (!browserInfo.version || browserInfo.version === "Unknown") {
                return browserInfo.browser;
            }
            return `${browserInfo.browser} ${browserInfo.version}`;
        },

        async getBrowserInfo(): Promise<BrowserInfo> {
            const userAgent = navigator.userAgent;
            const userAgentData = (navigator as any).userAgentData;
            let browser = "Unknown";
            let version = "Unknown";
            let os = "Unknown";
            let osVersion = "Unknown";

            const mapBrowserBrand = (brandName: string): string => {
                if (/edge|microsoft edge/i.test(brandName)) return "Edge";
                if (/opera/i.test(brandName)) return "Opera";
                if (/chromium|chrome|google chrome/i.test(brandName)) return "Chrome";
                if (/firefox/i.test(brandName)) return "Firefox";
                if (/safari/i.test(brandName)) return "Safari";
                return "Unknown";
            };

            // Prefer User-Agent Client Hints when available.
            if (userAgentData && Array.isArray(userAgentData.brands)) {
                const brandMatch = userAgentData.brands.find((entry: any) => mapBrowserBrand(entry.brand) !== "Unknown");
                if (brandMatch) {
                    browser = mapBrowserBrand(brandMatch.brand);
                    version = brandMatch.version || version;
                }
            }

            if (userAgentData && typeof userAgentData.getHighEntropyValues === "function") {
                try {
                    const entropy = await userAgentData.getHighEntropyValues([
                        "platform",
                        "platformVersion",
                        "fullVersionList",
                        "uaFullVersion"
                    ]);

                    if (Array.isArray(entropy.fullVersionList)) {
                        const fullVersionMatch = entropy.fullVersionList.find((entry: any) => mapBrowserBrand(entry.brand) !== "Unknown");
                        if (fullVersionMatch) {
                            browser = mapBrowserBrand(fullVersionMatch.brand);
                            version = fullVersionMatch.version || version;
                        }
                    } else if (entropy.uaFullVersion && version === "Unknown") {
                        version = entropy.uaFullVersion;
                    }

                    const hintPlatform = String(entropy.platform || "").toLowerCase();
                    if (hintPlatform === "macos") {
                        os = "macOS";
                        osVersion = entropy.platformVersion || osVersion;
                    } else if (hintPlatform === "windows") {
                        os = "Windows";
                        osVersion = entropy.platformVersion || osVersion;
                    } else if (hintPlatform === "android") {
                        os = "Android";
                        osVersion = entropy.platformVersion || osVersion;
                    } else if (hintPlatform === "ios") {
                        os = "iOS";
                        osVersion = entropy.platformVersion || osVersion;
                    } else if (hintPlatform === "linux") {
                        os = "Linux";
                        osVersion = entropy.platformVersion || osVersion;
                    }
                } catch (error) {
                    // Fall back to user agent parsing if Client Hints are unavailable.
                }
            }

            // Fallback browser detection from the user-agent string.
            if (browser === "Unknown" && /edg/i.test(userAgent)) {
                browser = "Edge";
                version = userAgent.match(/edg\/([\d.]+)/i)?.[1] || "Unknown";
            } else if (browser === "Unknown" && /opr|opera/i.test(userAgent)) {
                browser = "Opera";
                version = userAgent.match(/(?:opr|opera)\/([\d.]+)/i)?.[1] || "Unknown";
            } else if (browser === "Unknown" && /chrome|chromium|crios/i.test(userAgent)) {
                browser = "Chrome";
                version = userAgent.match(/chrome\/([\d.]+)/i)?.[1] || "Unknown";
            } else if (browser === "Unknown" && /firefox|fxios/i.test(userAgent)) {
                browser = "Firefox";
                version = userAgent.match(/firefox\/([\d.]+)/i)?.[1] || "Unknown";
            } else if (browser === "Unknown" && /safari/i.test(userAgent) && !/chrome|chromium|crios|edg|opr|opera/i.test(userAgent)) {
                browser = "Safari";
                version = userAgent.match(/version\/([\d.]+)/i)?.[1] || "Unknown";
            } else if (browser === "Unknown" && /msie|trident/i.test(userAgent)) {
                browser = "Internet Explorer";
                version = userAgent.match(/(?:msie |rv:)(\d+(\.\d+)?)/i)?.[1] || "Unknown";
            }

            // Fallback OS parsing from user-agent string.
            if (os === "Unknown" && /windows nt/i.test(userAgent)) {
                os = "Windows";
                const windowsVersion = userAgent.match(/windows nt (\d+\.\d+)/i)?.[1];
                osVersion = windowsVersion
                    ? windowsVersion
                        .replace("10.0", "10")
                        .replace("6.3", "8.1")
                        .replace("6.2", "8")
                        .replace("6.1", "7")
                        .replace("6.0", "Vista")
                        .replace("5.1", "XP")
                    : "Unknown";
            } else if (os === "Unknown" && /mac os x/i.test(userAgent)) {
                os = "macOS";
                osVersion = userAgent.match(/mac os x (\d+(?:[\._]\d+){1,2})/i)?.[1]?.replace(/_/g, ".") || "Unknown";
            } else if (os === "Unknown" && /android/i.test(userAgent)) {
                os = "Android";
                osVersion = userAgent.match(/android (\d+(\.\d+)?)/i)?.[1] || "Unknown";
            } else if (os === "Unknown" && /iphone|ipad|ipod/i.test(userAgent)) {
                os = "iOS";
                osVersion = userAgent.match(/os (\d+(?:[\._]\d+){1,2})/i)?.[1]?.replace(/_/g, ".") || "Unknown";
            } else if (os === "Unknown" && /linux/i.test(userAgent)) {
                os = "Linux";
                osVersion = "Unknown";
            }

            return { browser, version, os, osVersion };
        },

        /**
         * Hide any open tooltip. (We only allow one open at a time.)
         */
        hideAllTooltips(): void {
            this.currentlyOpenButton = null;
            if (this.currentlyOpenTooltip) {
                this.currentlyOpenTooltip.remove();
                this.currentlyOpenTooltip = null;
            }
        },
        
        /**
         * Auto-position the tooltip above or below the button, depending on space.
         */
        /**
         * Position the tooltip below the button and ensure it is within the header's horizontal boundaries.
         * The tooltip is shown in the header and is always positioned below the attached button.
         */
        positionTooltip(button: HTMLButtonElement, tooltip: HTMLElement): void {
            const margin = -20; // gap between the button and the tooltip
          
            // Ensure the tooltip is appended to <body> so it isn't constrained by parent containers
            if (!document.body.contains(tooltip)) {
              document.body.appendChild(tooltip);
            }
          
            // Force absolute positioning (relative to the viewport)
            tooltip.style.position = 'fixed';
            tooltip.style.zIndex = '9999';   // high stacking order to appear on top
            tooltip.style.visibility = 'hidden'; // hide initially for accurate measurement
            tooltip.style.minWidth = '140px'; // prevent text wrapping in menu items
            tooltip.style.whiteSpace = 'nowrap'; // ensure menu items don't wrap
          
            // Get the button's bounding rect (viewport coords)
            const buttonRect = button.getBoundingClientRect();
          
            // Force a reflow to ensure styles are applied before measuring
            tooltip.offsetHeight;
            
            // Measure the tooltip size after styles are applied
            const tooltipRect = tooltip.getBoundingClientRect();
          
            // Position tooltip directly below the button using viewport coordinates
            let top = buttonRect.bottom + margin;
            
            // Try to center first, but prioritize staying on screen
            let left = buttonRect.left + (buttonRect.width / 2) - (tooltipRect.width / 2);
            
            // Check if centering would cause cutoff and adjust accordingly
            const screenMargin = 10; // margin from screen edge
            const wouldBeOffRight = left + tooltipRect.width + screenMargin > window.innerWidth;
            const wouldBeOffLeft = left < screenMargin;
            
            if (wouldBeOffRight) {
              // Align to right edge of button instead of centering
              left = buttonRect.right - tooltipRect.width;
            }
            
            // Final clamp to screen boundaries
            if (left < screenMargin) {
              left = screenMargin;
            }
            const maxLeft = window.innerWidth - tooltipRect.width - screenMargin;
            if (left > maxLeft) {
              left = maxLeft;
            }
          
            // Ensure tooltip doesn't go off the bottom of the screen
            const maxTop = window.innerHeight - tooltipRect.height - margin;
            if (top > maxTop) {
              // Position above the button instead
              top = buttonRect.top - tooltipRect.height - margin;
            }
          
            // Apply final positioning
            tooltip.style.left = `${left}px`;
            tooltip.style.top = `${top}px`;
            tooltip.style.visibility = 'visible';
          
            // Calculate arrow position relative to the tooltip
            const arrowLeft = (buttonRect.left + buttonRect.width / 2) - left;
            tooltip.style.setProperty('--arrow-left', `${arrowLeft}px`);
        },

        /**
         * Attach a tooltip to the given button.
         * - A single click toggles the tooltip.
         * - The tooltip content is set using innerHTML, so you can include HTML (e.g. pop-up menus).
         */
        attachTooltip(element: HTMLElement, content: string | HTMLElement, afterShow: Function): void {
            // Remove any existing click handler to avoid multiple listeners
            if ((element as any)._tripistClickHandler) {
                element.removeEventListener('click', (element as any)._tripistClickHandler);
            }
            
            // Create new click handler
            const clickHandler = (evt: Event) => {
                // If clicking the same button that's already open, close it
                if (this.currentlyOpenButton === element && this.currentlyOpenTooltip) {
                    this.hideAllTooltips();
                    return;
                }
                
                // Hide any open tooltip, then create a new one.
                this.hideAllTooltips();
                const tooltip = document.createElement('div');
                tooltip.className = 'tooltip tooltip--below'; // default placement; will be updated in positionTooltip.
        
                if (typeof content === 'string') {
                    tooltip.innerHTML = content;
                } else {
                    // Clone the HTMLElement to avoid moving it from its current position in the DOM.
                    tooltip.appendChild(content);
                }
                
                document.body.appendChild(tooltip);
                this.currentlyOpenButton = element;
                this.currentlyOpenTooltip = tooltip;
                this.positionTooltip(element, tooltip);

                afterShow();
            };
            
            // Store reference to the handler so we can remove it later
            (element as any)._tripistClickHandler = clickHandler;
            
            // Add the new event listener
            element.addEventListener('click', clickHandler);
        },

        initializeFeedbackModal(): void {
            if (!this.feedbackModal || !this.feedbackStarsContainer || !this.feedbackCommentField || !this.feedbackSubmitButton) {
                console.warn("Tripist: Feedback modal elements missing, skipping initialization.");
                return;
            }

            const starEmptyUrl = `${this.baseUrl}/assets/default/star-empty.svg`;
            const starFilledUrl = `${this.baseUrl}/assets/default/star-filled.svg`;

            const modalContent = this.feedbackModal.querySelector('.tripist-feedback-modal-content') as HTMLElement | null;
            if (modalContent) {
                modalContent.style.display = 'flex';
                modalContent.style.flexDirection = 'column';
                modalContent.style.gap = '16px';
                modalContent.style.padding = '24px';
                modalContent.style.maxWidth = modalContent.style.maxWidth || '400px';
                modalContent.style.boxSizing = 'border-box';
            }

            this.feedbackStarsContainer.style.display = 'flex';
            this.feedbackStarsContainer.style.gap = '12px';
            this.feedbackStarsContainer.style.justifyContent = 'center';
            this.feedbackStarsContainer.setAttribute('data-star-empty-url', starEmptyUrl);
            this.feedbackStarsContainer.setAttribute('data-star-filled-url', starFilledUrl);

            this.feedbackCommentField.style.width = '100%';
            this.feedbackCommentField.style.minHeight = this.feedbackCommentField.style.minHeight || '100px';
            this.feedbackCommentField.style.padding = this.feedbackCommentField.style.padding || '12px';
            this.feedbackCommentField.style.boxSizing = 'border-box';
            this.feedbackCommentField.style.margin = '0';

            this.feedbackSubmitButton.style.alignSelf = 'stretch';
            this.feedbackSubmitButton.style.margin = '0';

            this.feedbackEscapeHandler = (event: KeyboardEvent) => {
                if (event.key === "Escape") {
                    this.hideFeedbackModal();
                }
            };

            this.feedbackCommentField.value = "";
            this.feedbackSubmitButton.disabled = true;
            this.currentFeedbackRating = 0;

            this.feedbackStarsContainer.innerHTML = "";
            for (let i = 1; i <= 5; i++) {
                const starButton = document.createElement("button");
                starButton.type = "button";
                starButton.classList.add("tripist-feedback-star-empty");
                starButton.dataset.ratingValue = i.toString();
                starButton.style.width = '32px';
                starButton.style.height = '32px';
                starButton.style.backgroundRepeat = 'no-repeat';
                starButton.style.backgroundSize = 'contain';
                starButton.style.backgroundImage = `url('${starEmptyUrl}')`;
                starButton.addEventListener("click", () => {
                    this.setFeedbackRating(i);
                });
                this.feedbackStarsContainer.appendChild(starButton);
            }

            this.feedbackSubmitButton.addEventListener("click", async () => {
                if (!this.feedbackSubmitButton?.disabled) {
                    await this.submitFeedback();
                }
            });

            this.feedbackModal.addEventListener("click", (event: Event) => {
                if (event.target === this.feedbackModal) {
                    this.hideFeedbackModal();
                }
            });
        },

        setFeedbackRating(rating: number): void {
            this.currentFeedbackRating = rating;

            if (!this.feedbackStarsContainer) {
                return;
            }

            const starEmptyUrl = this.feedbackStarsContainer.getAttribute('data-star-empty-url') || `${this.baseUrl}/assets/default/star-empty.svg`;
            const starFilledUrl = this.feedbackStarsContainer.getAttribute('data-star-filled-url') || `${this.baseUrl}/assets/default/star-filled.svg`;

            const starButtons = Array.from(this.feedbackStarsContainer.querySelectorAll("button") as NodeListOf<HTMLButtonElement>);
            starButtons.forEach((button, index) => {
                const starIndex = index + 1;
                button.classList.toggle("tripist-feedback-star-filled", starIndex <= rating);
                button.classList.toggle("tripist-feedback-star-empty", starIndex > rating);
                button.style.backgroundImage = `url('${starIndex <= rating ? starFilledUrl : starEmptyUrl}')`;
            });

            if (this.feedbackSubmitButton) {
                this.feedbackSubmitButton.disabled = rating === 0;
            }
        },

        showFeedbackModal(): void {
            if (!this.feedbackModal) {
                console.warn("Tripist: Feedback modal not available");
                return;
            }

            this.setFeedbackRating(this.currentFeedbackRating > 0 ? this.currentFeedbackRating : 0);

            if (this.feedbackCommentField) {
                this.feedbackCommentField.value = "";
            }

            this.feedbackModal.setAttribute("aria-hidden", "false");
            document.addEventListener("keydown", this.feedbackEscapeHandler as EventListener);
        },

        hideFeedbackModal(): void {
            if (!this.feedbackModal) {
                return;
            }

            this.feedbackModal.setAttribute("aria-hidden", "true");
            if (this.feedbackEscapeHandler) {
                document.removeEventListener("keydown", this.feedbackEscapeHandler);
            }
        },

        async submitFeedback(): Promise<void> {
            if (!this.thread) {
                console.warn("Tripist: Cannot submit feedback without an active thread");
                return;
            }

            if (this.currentFeedbackRating <= 0) {
                console.warn("Tripist: Cannot submit feedback without a rating");
                return;
            }

            const feedbackPayload: Record<string, any> = {
                rating: this.currentFeedbackRating,
            };

            if (this.feedbackCommentField && this.feedbackCommentField.value.trim().length > 0) {
                feedbackPayload.feedback = this.feedbackCommentField.value.trim();
            }

            try {
                if (this.feedbackSubmitButton) {
                    this.feedbackSubmitButton.disabled = true;
                }

                const response = await fetch(`${this.apiBaseUrl}/tripist/${this.integrationId}/thread/${this.thread}`, {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": this.getLocalStorage("auth_token") || "",
                    },
                    body: JSON.stringify(feedbackPayload),
                });

                if (!response.ok) {
                    console.error(`Tripist: Failed to submit feedback. Status: ${response.status}`);
                    if (this.feedbackSubmitButton) {
                        this.feedbackSubmitButton.disabled = false;
                    }
                    return;
                }

                this.hideFeedbackModal();
            } catch (error) {
                console.error("Tripist: Error submitting feedback", error);
                if (this.feedbackSubmitButton) {
                    this.feedbackSubmitButton.disabled = false;
                }
            }
        },

        async destroy(): Promise<void> {
            if (!this.container) {
                console.warn("Tripist: Cannot destroy widget - container not found");
                return;
            }
            
            console.log("Tripist: Destroying widget");
            
            // Clear the container's content
            this.container.innerHTML = '';
            
            // Unsubscribe from Firebase auth state changes
            if (this.firebaseAuthUnsubscribe) {
                this.firebaseAuthUnsubscribe();
                this.firebaseAuthUnsubscribe = null;
            }
            
            // Properly delete Firebase app instance and WAIT for it to complete
            if (this.firebaseApp) {
                try {
                    await deleteApp(this.firebaseApp);
                    console.log("Tripist: Firebase app deleted successfully");
                } catch (error) {
                    console.error("Tripist: Error deleting Firebase app:", error);
                }
            }
            
            // Reset Firebase and authentication state
            this.firebaseApp = null;
            this.firebaseAnalytics = null;
            this.firebaseAuth = null;
            this.firebaseUser = null;
            this.lhpeDevice = null;
            this.lhpeUser = null;
            this.identityProviderType = null;
            this.identityProviderConfig = null;
            this.deviceIdentifier = null;
            this.isIdentified = false;
            this.onReady = null;
            this.onIdentify = null;
            this.onIdentifyError = null;
            
            // Reset configuration and thread state
            this.config = null;
            this.thread = null;
            this.chatState = null;
            this.selectedCategories = [];
            this.downloadUrl = null;
            this.exploristResultsData = null;
            this.currentTempOutboundId = null;
            this.currentTempInboundId = null;
            this.currentMessageHasToolCalls = false;
            this.currentFeedbackRating = 0;
            
            // Reset initialization flag AFTER everything is cleaned up
            this.isInitializing = false;
            
            // Reset all DOM element references
            this.header = null;
            this.logoText = null;
            this.menuButton = null;
            this.closeButton = null;
            this.hud = null;
            this.hudMessage = null;
            this.hudButton = null;
            this.body = null;
            this.footerContainer = null;
            this.catContainer = null;
            this.catTitle = null;
            this.catDescr = null;
            this.catList = null;
            this.catButton = null;
            this.chatContainer = null;
            this.chatSendButton = null;
            this.chatInputField = null;
            this.chatMessagesContainer = null;
            this.chatIntroContainer = null;
            this.chatIntroTitle = null;
            this.chatIntroDescr = null;
            this.inputContainer = null;
            this.textInput = null;
            this.sendButton = null;
            this.actionsContainer = null;
            this.authContainer = null;
            this.loginEmail = null;
            this.loginPassword = null;
            this.loginButton = null;
            this.forgotPasswordLink = null;
            this.registerFirstName = null;
            this.registerLastName = null;
            this.registerEmail = null;
            this.registerPassword = null;
            this.registerConfirmPassword = null;
            this.registerLocation = null;
            this.registerButton = null;
            this.profileContainer = null;
            this.profileFirstName = null;
            this.profileLastName = null;
            this.profileEmail = null;
            this.profileLocation = null;
            this.profileAvatarTitle = null;
            this.profileAvatarSubtitle = null;
            this.profileAvatarContainer = null;
            this.profileButton = null;
            this.profileCancelButton = null;
            this.exploristResultsContainer = null;
            this.exploristResultsHeader = null;
            this.exploristResultsTitle = null;
            this.exploristResultsMessage = null;
            this.exploristResultsList = null;
            this.feedbackModal = null;
            this.feedbackStarsContainer = null;
            this.feedbackCommentField = null;
            this.feedbackSubmitButton = null;
            
            // Reset state
            this.currentState = AppState.Default;
            this.previousState = AppState.Default;

            // Remove injected widget styles so new initializations can load fresh assets.
            document.querySelectorAll('link[id^="tripist-"], style[id^="tripist-"]').forEach((el) => el.remove());
        },

    };

    (window as any).tripist = tripist;

})(window, document);
